import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Circle, Tooltip as LeafletTooltip, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popover,
  Slider,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LayersIcon from '@mui/icons-material/Layers';
import MapIcon from '@mui/icons-material/Map';
import TuneIcon from '@mui/icons-material/Tune';
import BoltIcon from '@mui/icons-material/Bolt';
import StopIcon from '@mui/icons-material/Stop';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import BalanceIcon from '@mui/icons-material/Balance';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import VibrationIcon from '@mui/icons-material/Vibration';
import NearMeIcon from '@mui/icons-material/NearMe';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PlaceIcon from '@mui/icons-material/Place';
import Badge from '@mui/material/Badge';
import {
  api,
  type DemandSupplyResponse,
  type MasterToolProjectResult,
  type MasterToolSearchFilters,
  type MasterToolUnit,
  type PricePromoUnit,
  type ThumbsupUnit,
} from '../../services/api';
import { formatOnemapLocationLabel, parseOnemapCoordinates, searchOnemapLocations, type OneMapSearchResult } from '../../utils/onemapLocationSearch';
import ProjectPopupCard, { type MapDealFilter } from './ProjectPopupCard';
import MapResearchCriteriaPanel from './MapResearchCriteriaPanel';
import CompareCartDialog, { COMPARE_CART_MAX } from './CompareCartDialog';
import { DemandSupplyPanel, DemandSupplyMapMarkers } from '../map';
import DemandSupplyDetailCard, { type DemandSupplySelection } from '../map/DemandSupplyDetailCard';
import type { SelectedDemandSupplyTypes } from '../../../types';
import {
  BASEMAPS,
  DEFAULT_RADIUS_KM,
  FLASH_INTERVAL_MAX_MS,
  FLASH_INTERVAL_MIN_MS,
  FLASH_INTERVAL_MS,
  MASTER_PLANS,
  RADIUS_MAX_KM,
  RADIUS_MIN_KM,
  RADIUS_STEP_KM,
  SG_CENTER,
  SG_MAX_BOUNDS,
  SG_MAX_ZOOM,
  SG_MIN_ZOOM,
  URA_ATTRIBUTION,
} from './mapResearchConfig';
/**
 * Map Research — every active project plotted on an interactive Singapore map,
 * with OneMap location search, an adjustable radius scope, Price Promo /
 * Thumbsup deal filters, switchable basemaps and URA Master Plan overlays
 * (including a two-plan "Flash" comparison mode).
 */

interface DealGroup<TUnit> {
  projectId: string;
  projectName: string;
  units: TUnit[];
}

interface MapResearchTabProps {
  pricePromoGroups: Array<DealGroup<PricePromoUnit>>;
  thumbsupGroups: Array<DealGroup<ThumbsupUnit>>;
  canFilterPricePromo: boolean;
  canFilterThumbsup: boolean;
  /** Whether the authenticated session has loaded the Super Ratio capability. */
  canUseSuperRatio?: boolean;
  onViewProject: (projectId: string) => void;
  /** Opens Price History for a promo unit from the project popup. */
  onPricePromoUnitClick?: (unit: PricePromoUnit) => void;
  /** Navigates to the project and sets the thumbs unit as reference. */
  onThumbsupUnitClick?: (unit: ThumbsupUnit) => void;
}

interface FocusPoint {
  lat: number;
  lng: number;
  label: string;
}

interface MapProject {
  project: MasterToolProjectResult;
  id: string;
  lat: number;
  lng: number;
}

/** GLS pipeline rows use names ending in "GLS" — keep them off Super Map. */
const isGlsProjectName = (name: string | null | undefined): boolean =>
  typeof name === 'string' && name.trim().toUpperCase().endsWith('GLS');

type MarkerKind = 'project' | 'promo' | 'thumb';

const MARKER_PALETTE: Record<MarkerKind, { core: string; glow: string; icon: string }> = {
  project: {
    core: 'linear-gradient(135deg, #67e8f9 0%, #3b82f6 100%)',
    glow: 'rgba(34, 211, 238, 0.65)',
    // MUI Apartment path
    icon: 'M17 11V3H7v4H3v14h8v-4h2v4h8V11h-4zM7 19H5v-2h2v2zm0-4H5v-2h2v2zm0-4H5V9h2v2zm4 4H9v-2h2v2zm0-4H9V9h2v2zm0-4H9V5h2v2zm4 8h-2v-2h2v2zm0-4h-2V9h2v2zm0-4h-2V5h2v2zm4 12h-2v-2h2v2zm0-4h-2v-2h2v2z',
  },
  promo: {
    core: 'linear-gradient(135deg, #fde68a 0%, #f59e0b 100%)',
    glow: 'rgba(251, 191, 36, 0.7)',
    // MUI LocalOffer path
    icon: 'M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z',
  },
  thumb: {
    core: 'linear-gradient(135deg, #86efac 0%, #22c55e 100%)',
    glow: 'rgba(34, 197, 94, 0.65)',
    // MUI ThumbUp path
    icon: 'M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z',
  },
};

const dealFilterToMarkerKind = (filter: MapDealFilter): MarkerKind => {
  if (filter === 'promo') return 'promo';
  if (filter === 'thumbsup') return 'thumb';
  return 'project';
};

const haversineKm = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const glassPanelSx = {
  bgcolor: 'rgba(10, 20, 34, 0.86)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(34, 211, 238, 0.28)',
  borderRadius: '12px',
  boxShadow: '0 12px 40px rgba(2, 8, 20, 0.5), 0 0 20px rgba(34, 211, 238, 0.08)',
  color: '#eef6fa',
};

const microLabelSx = {
  fontFamily: '"SF Mono", "Roboto Mono", Menlo, monospace',
  fontSize: 9.5,
  letterSpacing: '0.14em',
  textTransform: 'uppercase' as const,
  color: 'rgba(148, 197, 222, 0.8)',
};

/** Frame for a whole-island Super Ratio scan. */
const SG_MAP_BOUNDS = L.latLngBounds([1.15, 103.6], [1.48, 104.1]);
/**
 * Centre for an island-wide scan with no location picked. The scan ignores
 * distance, but the analysis still needs an origin — markers measure from here.
 */
const SG_CENTRE: FocusPoint = { lat: 1.3521, lng: 103.8198, label: 'Whole Singapore' };
/** Scan radius a keyed-in location falls back to, coming off an island-wide scan. */
const SUPER_RATIO_FOCUS_RADIUS_KM = 2;

/** Nearby Price Promo scan — matches the default Super Map radius. */
const NEARBY_PROMO_RADIUS_KM = 5;
/** Spike size for a shake impulse. Prefers linear acceleration (no gravity). */
const SHAKE_THRESHOLD = 14;
/** Fallback when only accelerationIncludingGravity is available (includes tilt). */
const SHAKE_THRESHOLD_WITH_GRAVITY = 32;
/** Need several spikes in a short burst — a single lift/put-down is one impulse. */
const SHAKE_REQUIRED_HITS = 3;
const SHAKE_HIT_WINDOW_MS = 900;
/** After a shake toggles YaoYao, ignore further shakes so one gesture can't flip on→off. */
const SHAKE_COOLDOWN_MS = 2500;
const YAOYAO_STORAGE_KEY = 'super-map-yaoyao-enabled';
const COMPARE_CART_STORAGE_KEY = 'super-map-compare-cart';

const readYaoyaoPreference = (): boolean => {
  try {
    return window.localStorage.getItem(YAOYAO_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const writeYaoyaoPreference = (enabled: boolean) => {
  try {
    window.localStorage.setItem(YAOYAO_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Ignore quota / private-mode failures.
  }
};

const readCompareCart = (): MasterToolProjectResult[] => {
  try {
    const raw = window.sessionStorage.getItem(COMPARE_CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (item): item is MasterToolProjectResult =>
          Boolean(item) && typeof item === 'object' && typeof (item as MasterToolProjectResult).id === 'string',
      )
      .slice(0, COMPARE_CART_MAX);
  } catch {
    return [];
  }
};

const writeCompareCart = (projects: MasterToolProjectResult[]) => {
  try {
    if (projects.length === 0) {
      window.sessionStorage.removeItem(COMPARE_CART_STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(COMPARE_CART_STORAGE_KEY, JSON.stringify(projects.slice(0, COMPARE_CART_MAX)));
  } catch {
    // Ignore quota / private-mode failures.
  }
};

/** iPhone / iPad — Shake to Undo is on by default and cannot be queried from the web. */
const isIosDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  // iPadOS desktop UA
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
};

/** Captures the Leaflet map instance and closes the popup on bare-map clicks. */
const MapBridge: React.FC<{ mapRef: React.MutableRefObject<L.Map | null>; onBareClick: () => void }> = ({
  mapRef,
  onBareClick,
}) => {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);
  useMapEvents({
    click: (e) => {
      // Clicks that originated on a marker/path bubble to the map — ignore those.
      if ((e as L.LeafletMouseEvent & { propagatedFrom?: unknown }).propagatedFrom) return;
      onBareClick();
    },
  });
  return null;
};

const MapResearchTab: React.FC<MapResearchTabProps> = ({
  pricePromoGroups,
  thumbsupGroups,
  canFilterPricePromo,
  canFilterThumbsup,
  canUseSuperRatio = false,
  onViewProject,
  onPricePromoUnitClick,
  onThumbsupUnitClick,
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const iconCacheRef = useRef(new Map<string, L.DivIcon>());
  /** Skip OneMap autocomplete when focus is set programmatically (e.g. Super Ratio from popup). */
  const skipSearchAutocompleteRef = useRef(false);
  const superRatioActiveRef = useRef(false);
  const superRatioNationwideRef = useRef(false);
  const superRatioSelectionRef = useRef<DemandSupplySelection | null>(null);

  // Project data — baseline (all projects) vs criteria-filtered results
  const [allProjects, setAllProjects] = useState<MasterToolProjectResult[]>([]);
  const [projects, setProjects] = useState<MasterToolProjectResult[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Unit criteria (Project Research filters)
  const [criteriaFilters, setCriteriaFilters] = useState<MasterToolSearchFilters>({ priceMin: '1' });
  const [criteriaActive, setCriteriaActive] = useState(false);
  const [criteriaLoading, setCriteriaLoading] = useState(false);
  const [criteriaError, setCriteriaError] = useState<string | null>(null);
  const [matchedUnitTotal, setMatchedUnitTotal] = useState(0);
  const [matchedUnits, setMatchedUnits] = useState<MasterToolUnit[]>([]);
  const [matchedUnitsLoading, setMatchedUnitsLoading] = useState(false);
  const [matchedUnitsError, setMatchedUnitsError] = useState<string | null>(null);
  /** Matched criteria units grouped by project id — filled on marker click / deal AND. */
  const [criteriaUnitsByProjectId, setCriteriaUnitsByProjectId] = useState<Record<string, MasterToolUnit[]>>({});
  const [dealMetaLoading, setDealMetaLoading] = useState(false);

  // Location search
  const [searchQuery, setSearchQuery] = useState('');
  const [placeResults, setPlaceResults] = useState<OneMapSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [focus, setFocus] = useState<FocusPoint | null>(null);
  const [radiusKm, setRadiusKm] = useState(DEFAULT_RADIUS_KM);
  // Inner edge of the Super Ratio scan ring in km; 0 scans from the focus outwards.
  // Only adjustable while Super Ratio owns the map — the other research tools
  // still work off a plain disc, so the inner edge resets when it turns off.
  const [minRadiusKm, setMinRadiusKm] = useState(0);

  // Layers
  const [basemapId, setBasemapId] = useState(BASEMAPS[0].id);
  const [basemapAnchor, setBasemapAnchor] = useState<HTMLElement | null>(null);
  const [criteriaAnchor, setCriteriaAnchor] = useState<HTMLElement | null>(null);
  const [masterPlanAnchor, setMasterPlanAnchor] = useState<HTMLElement | null>(null);
  const [masterPlanId, setMasterPlanId] = useState<string | null>(null);
  const [masterPlanOpacity, setMasterPlanOpacity] = useState(0.65);

  // Flash comparison
  const [flashPicks, setFlashPicks] = useState<string[]>([]);
  const [flashRunning, setFlashRunning] = useState(false);
  const [flashIndex, setFlashIndex] = useState(0);
  const [flashIntervalMs, setFlashIntervalMs] = useState(FLASH_INTERVAL_MS);

  // Deal filter
  const [dealFilter, setDealFilter] = useState<MapDealFilter>('none');
  /** Price Promo limited to ~5 km of the user's current location (phone / shake). */
  const [nearbyPromoActive, setNearbyPromoActive] = useState(false);
  const [nearbyPromoLocating, setNearbyPromoLocating] = useState(false);
  /** Shake-to-scan (YaoYao). Off by default; iOS still needs a motion-permission grant. */
  const [yaoyaoEnabled, setYaoyaoEnabled] = useState(readYaoyaoPreference);
  const [yaoyaoPermissionBusy, setYaoyaoPermissionBusy] = useState(false);
  const [yaoyaoMotionHelpOpen, setYaoyaoMotionHelpOpen] = useState(false);
  const [yaoyaoShakeUndoHelpOpen, setYaoyaoShakeUndoHelpOpen] = useState(false);
  /** Mobile: radius slider stays collapsed until the user expands it. */
  const [mobileRadiusExpanded, setMobileRadiusExpanded] = useState(false);
  const nearbyPromoActiveRef = useRef(false);
  const nearbyPromoLocatingRef = useRef(false);
  const yaoyaoEnabledRef = useRef(false);
  const lastShakeAtRef = useRef(0);
  const toggleNearbyPromoRef = useRef<() => void>(() => {});
  nearbyPromoActiveRef.current = nearbyPromoActive;
  nearbyPromoLocatingRef.current = nearbyPromoLocating;
  yaoyaoEnabledRef.current = yaoyaoEnabled;

  // Super Ratio (demand / supply)
  const [superRatioActive, setSuperRatioActive] = useState(false);
  // Whole-island Super Ratio scan — both ends of the scan ring are ignored.
  const [superRatioNationwide, setSuperRatioNationwide] = useState(false);
  const [superRatioData, setSuperRatioData] = useState<DemandSupplyResponse | null>(null);
  const [superRatioLoading, setSuperRatioLoading] = useState(false);
  const [superRatioError, setSuperRatioError] = useState<string | null>(null);
  const [superRatioSelection, setSuperRatioSelection] = useState<DemandSupplySelection | null>(null);
  const [projectBodyCollapsed, setProjectBodyCollapsed] = useState(false);
  const [compareCart, setCompareCart] = useState<MasterToolProjectResult[]>(readCompareCart);
  const [compareCartOpen, setCompareCartOpen] = useState(false);
  const [compareFabPulse, setCompareFabPulse] = useState(false);
  const [compareThrow, setCompareThrow] = useState<{
    key: number;
    label: string;
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    /** Peak height of the toss arc, in px. */
    lift: number;
  } | null>(null);
  const compareFabRef = useRef<HTMLButtonElement | null>(null);
  const [selectedDemandSupplyTypes, setSelectedDemandSupplyTypes] = useState<SelectedDemandSupplyTypes>({
    hdbBtoMop: true,
    hdbResaleMop: true,
    // Matches the Super Ratio tab: Condo/EC starts off and the user opts in.
    condoSsd: false,
    newLaunch: true,
    gls: true,
  });
  superRatioActiveRef.current = superRatioActive;
  superRatioNationwideRef.current = superRatioNationwide;
  superRatioSelectionRef.current = superRatioSelection;

  useEffect(() => {
    // Marker detail open → tuck project intel into the dropdown header.
    setProjectBodyCollapsed(Boolean(superRatioSelection));
  }, [superRatioSelection]);

  // Survive tab switches (Super Map unmounts when leaving) within this browser session.
  useEffect(() => {
    writeCompareCart(compareCart);
  }, [compareCart]);

  const toggleCompareProject = useCallback((project: MasterToolProjectResult, event?: React.MouseEvent) => {
    if (!project.id) return;

    setCompareCart((prev) => {
      const exists = prev.some((p) => p.id === project.id);
      if (exists) return prev.filter((p) => p.id !== project.id);
      if (prev.length >= COMPARE_CART_MAX) return prev;
      return [...prev, project];
    });

    // Only animate when adding (not removing / full cart).
    const alreadyInCart = compareCart.some((p) => p.id === project.id);
    const cartFull = compareCart.length >= COMPARE_CART_MAX;
    if (alreadyInCart || cartFull || !event || !compareFabRef.current) return;

    const fabRect = compareFabRef.current.getBoundingClientRect();
    const endX = fabRect.left + fabRect.width / 2;
    const endY = fabRect.top + fabRect.height / 2;
    const distance = Math.hypot(endX - event.clientX, endY - event.clientY);
    setCompareThrow({
      key: Date.now(),
      label: project.name?.trim() || 'Project',
      startX: event.clientX,
      startY: event.clientY,
      endX,
      endY,
      lift: Math.min(190, Math.max(70, distance * 0.3)),
    });
    // The chip lands as the 0.95s toss keyframes finish.
    window.setTimeout(() => {
      setCompareFabPulse(true);
      window.setTimeout(() => setCompareFabPulse(false), 900);
    }, 880);
    window.setTimeout(() => setCompareThrow(null), 1000);
  }, [compareCart]);

  const removeCompareProject = useCallback((projectId: string) => {
    setCompareCart((prev) => prev.filter((p) => p.id !== projectId));
  }, []);

  const clearCompareCart = useCallback(() => {
    setCompareCart([]);
  }, []);

  const compareCartIds = useMemo(
    () => new Set(compareCart.map((p) => p.id).filter((id): id is string => Boolean(id))),
    [compareCart],
  );

  const basemap = BASEMAPS.find((b) => b.id === basemapId) ?? BASEMAPS[0];
  const activeMasterPlan = MASTER_PLANS.find((mp) => mp.id === masterPlanId) ?? null;

  const fetchProjects = useCallback(async () => {
    try {
      setProjectsLoading(true);
      setProjectsError(null);
      const response = await api.searchProjects({ includeProjectsWithoutUnits: true });
      const nextProjects = response.projects.filter((project) => !isGlsProjectName(project.name));
      setAllProjects(nextProjects);
      setProjects(nextProjects);
      setCriteriaActive(false);
      setMatchedUnitTotal(0);
    } catch (err) {
      setProjectsError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchProjects();
  }, [fetchProjects]);

  // Debounced OneMap place suggestions while typing.
  useEffect(() => {
    const query = searchQuery.trim();
    if (skipSearchAutocompleteRef.current) {
      // Keep skip armed until the user types again — avoids reopening after a pick.
      setPlaceResults([]);
      setSearchLoading(false);
      return;
    }
    if (query.length < 3) {
      setPlaceResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const results = await searchOnemapLocations(query);
        if (!cancelled) {
          setPlaceResults(results.slice(0, 8));
          setSearchOpen(true);
        }
      } catch {
        if (!cancelled) setPlaceResults([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchQuery]);

  /** All plottable projects — used by location search even when criteria narrows the map. */
  const searchableMapProjects = useMemo<MapProject[]>(() => {
    return allProjects
      .filter((project) => !isGlsProjectName(project.name))
      .map((project) => {
        const lat = Number(project.mapLatitude);
        const lng = Number(project.mapLongitude);
        if (!project.id || !Number.isFinite(lat) || !Number.isFinite(lng) || lat === 0 || lng === 0) return null;
        return { project, id: project.id, lat, lng };
      })
      .filter((entry): entry is MapProject => entry !== null);
  }, [allProjects]);

  const projectSearchMatches = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (query.length < 2) return [] as MapProject[];

    const scored = searchableMapProjects
      .map((entry) => {
        const name = (entry.project.name || '').toLowerCase();
        const address = (entry.project.address || '').toLowerCase();
        const district = String(entry.project.district || '').toLowerCase();
        const developer = (entry.project.developer || '').toLowerCase();
        const mrt = (entry.project.nearestMrt || '').toLowerCase();
        const area = (entry.project.area || '').toLowerCase();
        const haystack = `${name} ${address} ${district} ${developer} ${mrt} ${area}`;
        if (!haystack.includes(query)) return null;
        // Prefer name prefix / contains for ranking.
        const rank =
          name === query ? 0 : name.startsWith(query) ? 1 : name.includes(query) ? 2 : 3;
        return { entry, rank };
      })
      .filter((row): row is { entry: MapProject; rank: number } => row !== null)
      .sort((a, b) => a.rank - b.rank || (a.entry.project.name || '').localeCompare(b.entry.project.name || ''));

    return scored.slice(0, 6).map((row) => row.entry);
  }, [searchQuery, searchableMapProjects]);

  const hasSearchSuggestions = projectSearchMatches.length > 0 || placeResults.length > 0;

  // Keep the dropdown open whenever local project matches appear (even before places load).
  useEffect(() => {
    if (skipSearchAutocompleteRef.current) return;
    if (projectSearchMatches.length > 0 && searchQuery.trim().length >= 2) {
      setSearchOpen(true);
    }
  }, [projectSearchMatches, searchQuery]);

  const fitToRadius = useCallback((lat: number, lng: number, km: number) => {
    const map = mapRef.current;
    if (!map) return;
    map.flyToBounds(L.latLng(lat, lng).toBounds(km * 2000), { padding: [40, 40], duration: 0.8 });
  }, []);

  const applyFocus = useCallback(
    (point: FocusPoint, radiusOverrideKm?: number) => {
      setFocus(point);
      setSearchOpen(false);
      setLocationError(null);
      fitToRadius(point.lat, point.lng, radiusOverrideKm ?? radiusKm);
    },
    [fitToRadius, radiusKm],
  );

  /**
   * Picking a location while Super Ratio is scanning the whole island narrows
   * it to a 2 km disc around that location instead of dropping the tool.
   * Returns true when it handled the hand-off, false when the caller should
   * fall back to turning Super Ratio off.
   */
  const scopeIslandScanToFocus = useCallback((): boolean => {
    if (!superRatioActiveRef.current || !superRatioNationwideRef.current) return false;
    setSuperRatioNationwide(false);
    setMinRadiusKm(0);
    setRadiusKm(SUPER_RATIO_FOCUS_RADIUS_KM);
    setSuperRatioSelection(null);
    return true;
  }, []);

  const turnOffSuperRatio = useCallback(() => {
    setSuperRatioActive(false);
    setSuperRatioNationwide(false);
    setMinRadiusKm(0);
    setSuperRatioData(null);
    setSuperRatioError(null);
    setSuperRatioSelection(null);
  }, []);

  const handlePickSearchResult = useCallback(
    (result: OneMapSearchResult) => {
      const coords = parseOnemapCoordinates(result);
      if (!coords) return;
      const label = formatOnemapLocationLabel(result);
      const scoped = scopeIslandScanToFocus();
      if (!scoped) turnOffSuperRatio();
      setNearbyPromoActive(false);
      setSelectedId(null);
      skipSearchAutocompleteRef.current = true;
      setSearchQuery(label);
      setPlaceResults([]);
      applyFocus({ ...coords, label }, scoped ? SUPER_RATIO_FOCUS_RADIUS_KM : undefined);
    },
    [applyFocus, scopeIslandScanToFocus, turnOffSuperRatio],
  );

  const readCurrentPosition = useCallback(
    (): Promise<{ lat: number; lng: number }> =>
      new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by this browser.'));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          () => reject(new Error('Unable to get your location. Check browser permissions.')),
          { enableHighAccuracy: true, timeout: 10000 },
        );
      }),
    [],
  );

  const handleUseCurrentLocation = useCallback(() => {
    setLocating(true);
    setLocationError(null);
    void readCurrentPosition()
      .then((coords) => {
        setLocating(false);
        const scoped = scopeIslandScanToFocus();
        if (!scoped) turnOffSuperRatio();
        setNearbyPromoActive(false);
        setSelectedId(null);
        applyFocus(
          { ...coords, label: 'Current location' },
          scoped ? SUPER_RATIO_FOCUS_RADIUS_KM : undefined,
        );
      })
      .catch((err: Error) => {
        setLocating(false);
        setLocationError(err.message || 'Unable to get your location.');
      });
  }, [applyFocus, readCurrentPosition, scopeIslandScanToFocus, turnOffSuperRatio]);

  const clearFocus = useCallback(() => {
    setFocus(null);
    setSearchQuery('');
    setPlaceResults([]);
    setSelectedId(null);
    setNearbyPromoActive(false);
    setMobileRadiusExpanded(false);
    // Super Ratio needs no location — dropping one returns it to the island.
    if (superRatioActiveRef.current) {
      setSuperRatioNationwide(true);
      setMinRadiusKm(0);
      setSuperRatioSelection(null);
      mapRef.current?.flyToBounds(SG_MAP_BOUNDS, { padding: [40, 40], duration: 0.8 });
      return;
    }
    turnOffSuperRatio();
  }, [turnOffSuperRatio]);

  const exitNearbyPromo = useCallback(() => {
    setNearbyPromoActive(false);
    setDealFilter('none');
    setFocus(null);
    setSearchQuery('');
    setPlaceResults([]);
    setSelectedId(null);
    setLocationError(null);
    setMobileRadiusExpanded(false);
  }, []);

  const enterNearbyPromo = useCallback(async () => {
    if (!canFilterPricePromo || superRatioActive || nearbyPromoLocatingRef.current) return;
    setNearbyPromoLocating(true);
    setLocationError(null);
    try {
      const coords = await readCurrentPosition();
      turnOffSuperRatio();
      setSelectedId(null);
      setRadiusKm(NEARBY_PROMO_RADIUS_KM);
      skipSearchAutocompleteRef.current = true;
      setSearchQuery('Current location');
      setPlaceResults([]);
      setSearchOpen(false);
      setFocus({ ...coords, label: 'Current location' });
      fitToRadius(coords.lat, coords.lng, NEARBY_PROMO_RADIUS_KM);
      setDealFilter('promo');
      setNearbyPromoActive(true);
    } catch (err) {
      setNearbyPromoActive(false);
      setLocationError(err instanceof Error ? err.message : 'Unable to get your location.');
    } finally {
      setNearbyPromoLocating(false);
    }
  }, [
    canFilterPricePromo,
    fitToRadius,
    readCurrentPosition,
    superRatioActive,
    turnOffSuperRatio,
  ]);

  const requestMotionPermission = useCallback(async (): Promise<'granted' | 'denied' | 'unsupported'> => {
    const DEM = DeviceMotionEvent as unknown as {
      requestPermission?: () => Promise<PermissionState | string>;
    };
    if (typeof DEM.requestPermission !== 'function') {
      // Android / desktop — no runtime prompt required.
      return 'unsupported';
    }
    try {
      // Must run inside a user gesture on iOS Safari.
      const result = await DEM.requestPermission();
      return result === 'granted' ? 'granted' : 'denied';
    } catch {
      return 'denied';
    }
  }, []);

  const setYaoyaoEnabledPersistent = useCallback((enabled: boolean) => {
    setYaoyaoEnabled(enabled);
    writeYaoyaoPreference(enabled);
  }, []);

  const handleYaoyaoToggle = useCallback(
    async (enable: boolean) => {
      if (!enable) {
        setYaoyaoEnabledPersistent(false);
        return;
      }
      setYaoyaoPermissionBusy(true);
      setLocationError(null);
      try {
        const result = await requestMotionPermission();
        if (result === 'denied') {
          setYaoyaoEnabledPersistent(false);
          setYaoyaoMotionHelpOpen(true);
          return;
        }
        // Shake to Undo is on by default and cannot be queried from the web —
        // warn on iPhone before activating so users turn it off first.
        if (isIosDevice()) {
          setYaoyaoShakeUndoHelpOpen(true);
          return;
        }
        setYaoyaoEnabledPersistent(true);
      } finally {
        setYaoyaoPermissionBusy(false);
      }
    },
    [requestMotionPermission, setYaoyaoEnabledPersistent],
  );

  const confirmYaoyaoShakeUndoOff = useCallback(() => {
    setYaoyaoShakeUndoHelpOpen(false);
    setYaoyaoEnabledPersistent(true);
  }, [setYaoyaoEnabledPersistent]);

  const cancelYaoyaoShakeUndoHelp = useCallback(() => {
    setYaoyaoShakeUndoHelpOpen(false);
    setYaoyaoEnabledPersistent(false);
  }, [setYaoyaoEnabledPersistent]);

  const toggleNearbyPromo = useCallback(() => {
    if (nearbyPromoLocatingRef.current) return;
    if (nearbyPromoActiveRef.current) {
      exitNearbyPromo();
      return;
    }
    void enterNearbyPromo();
  }, [enterNearbyPromo, exitNearbyPromo]);
  toggleNearbyPromoRef.current = toggleNearbyPromo;

  // Origin of the Super Ratio scan. An island-wide scan needs no location, so
  // it falls back to the island's midpoint until the user keys one in.
  const superRatioFocus = useMemo<FocusPoint | null>(
    () => focus ?? (superRatioNationwide ? SG_CENTRE : null),
    [focus, superRatioNationwide],
  );

  // Scan ring in meters — outer edge clamped to the slider bounds, inner edge
  // never past the outer one. Shared by the fetch, the map rings and the cart.
  const superRatioRadiusMeters = useMemo(
    () => Math.round(Math.min(Math.max(radiusKm, 0.5), 10) * 1000),
    [radiusKm],
  );
  const superRatioMinRadiusMeters = useMemo(
    () => Math.round(Math.max(0, Math.min(minRadiusKm, radiusKm)) * 1000),
    [minRadiusKm, radiusKm],
  );

  const scanRadiusLabel = superRatioActive && superRatioNationwide
    ? 'Whole Singapore'
    : superRatioActive && minRadiusKm > 0
      ? `${minRadiusKm.toFixed(1)}–${radiusKm.toFixed(1)} km`
      : `${radiusKm.toFixed(1)} km`;

  // Frame the island on the way in, and the scan ring again on the way out.
  const handleNationwideChange = useCallback((next: boolean) => {
    setSuperRatioNationwide(next);
    if (next) {
      mapRef.current?.flyToBounds(SG_MAP_BOUNDS, { padding: [40, 40], duration: 0.8 });
    } else if (focus) {
      fitToRadius(focus.lat, focus.lng, radiusKm);
    }
  }, [focus, radiusKm, fitToRadius]);

  // Fetch Super Ratio when active for the current focus + scan ring.
  useEffect(() => {
    if (!superRatioActive || !superRatioFocus || !canUseSuperRatio) return;

    let cancelled = false;
    const radiusMeters = superRatioRadiusMeters;
    const minRadiusMeters = superRatioMinRadiusMeters;
    const nationwide = superRatioNationwide;
    setSuperRatioLoading(true);
    setSuperRatioError(null);

    const timer = window.setTimeout(() => {
      setSuperRatioSelection(null);
      void (async () => {
        try {
          const data = await api.getDemandSupplyByLocation(
            superRatioFocus.lat,
            superRatioFocus.lng,
            radiusMeters,
            superRatioFocus.label,
            minRadiusMeters,
            nationwide,
          );
          if (!cancelled) setSuperRatioData(data);
        } catch (err) {
          if (!cancelled) {
            console.error('Map Research Super Ratio failed:', err);
            setSuperRatioData(null);
            setSuperRatioError(err instanceof Error ? err.message : 'Failed to load Super Ratio');
          }
        } finally {
          if (!cancelled) setSuperRatioLoading(false);
        }
      })();
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [superRatioActive, superRatioFocus, superRatioRadiusMeters, superRatioMinRadiusMeters, superRatioNationwide, canUseSuperRatio]);

  // Flash loop — alternate the two chosen plans until stopped.
  useEffect(() => {
    if (!flashRunning || flashPicks.length !== 2) return;
    const id = window.setInterval(() => setFlashIndex((i) => (i === 0 ? 1 : 0)), flashIntervalMs);
    return () => window.clearInterval(id);
  }, [flashRunning, flashPicks, flashIntervalMs]);

  const toggleFlashPick = useCallback((id: string) => {
    setFlashPicks((prev) => {
      if (prev.includes(id)) return prev.filter((p) => p !== id);
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
  }, []);

  const startFlash = useCallback(() => {
    if (flashPicks.length !== 2) return;
    setFlashIndex(0);
    setFlashRunning(true);
  }, [flashPicks]);

  const stopFlash = useCallback(() => setFlashRunning(false), []);

  const promoUnitIds = useMemo(() => {
    const ids = new Set<number>();
    for (const group of pricePromoGroups) {
      for (const unit of group.units) ids.add(unit.id);
    }
    return ids;
  }, [pricePromoGroups]);

  const thumbsupUnitIds = useMemo(() => {
    const ids = new Set<number>();
    for (const group of thumbsupGroups) {
      for (const unit of group.units) ids.add(unit.id);
    }
    return ids;
  }, [thumbsupGroups]);

  const groupUnitsByProjectId = useCallback(
    (units: MasterToolUnit[], projectList: MasterToolProjectResult[]): Record<string, MasterToolUnit[]> => {
      const nameToId = new Map<string, string>();
      for (const project of projectList) {
        const name = (project.name ?? '').trim().toLowerCase();
        if (project.id && name) nameToId.set(name, project.id);
      }
      const byProject: Record<string, MasterToolUnit[]> = {};
      for (const unit of units) {
        const fromId = unit.projectId?.trim() || null;
        const fromName = nameToId.get(unit.projectName.trim().toLowerCase()) ?? null;
        const projectId = fromId || fromName;
        if (!projectId) continue;
        if (!byProject[projectId]) byProject[projectId] = [];
        byProject[projectId].push(unit);
      }
      return byProject;
    },
    [],
  );

  /** Fetch matched criteria units, optionally limited to specific project names. */
  const fetchMatchedUnits = useCallback(
    async (projectNames?: string[]): Promise<MasterToolUnit[]> => {
      const pageSize = 100; // backend caps pageSize at 100
      const collected: MasterToolUnit[] = [];
      let page = 1;
      let total = 0;
      const names = projectNames?.map((n) => n.trim()).filter(Boolean);
      do {
        const unitResult = await api.searchUnits({
          filters: criteriaFilters,
          page,
          pageSize,
          ...(names && names.length > 0 ? { unitFilters: { projectName: names } } : {}),
        });
        total = unitResult.totalUnits;
        collected.push(...unitResult.items);
        if (unitResult.items.length === 0) break;
        page += 1;
      } while (collected.length < total);
      return collected;
    },
    [criteriaFilters],
  );

  const handleCriteriaSearch = useCallback(async () => {
    const hasReferenceLocation = Boolean(
      (criteriaFilters.locationLat ?? '').trim() && (criteriaFilters.locationLng ?? '').trim(),
    );
    const radiusValue = Number(criteriaFilters.radiusKm ?? '');
    const hasValidRadius = Number.isFinite(radiusValue) && radiusValue > 0;
    if (hasReferenceLocation && !hasValidRadius) {
      setCriteriaError('Enter a valid Radius (km) before searching.');
      return;
    }

    setCriteriaLoading(true);
    setCriteriaError(null);
    setSelectedId(null);
    setMatchedUnits([]);
    setMatchedUnitsError(null);
    setCriteriaUnitsByProjectId({});
    try {
      // Projects-only search — units load per marker click (and on deal-filter AND).
      const result = await api.searchProjects(criteriaFilters);
      setProjects(result.projects);
      setMatchedUnitTotal(result.totalMatchedUnits);
      setCriteriaActive(true);

      if (hasReferenceLocation && hasValidRadius) {
        const lat = Number(criteriaFilters.locationLat);
        const lng = Number(criteriaFilters.locationLng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          applyFocus({
            lat,
            lng,
            label: criteriaFilters.locationLabel || 'Criteria location',
          });
          setRadiusKm(radiusValue);
        }
      }
    } catch (err) {
      console.error('Map Research criteria search failed:', err);
      setCriteriaError(err instanceof Error ? err.message : 'Criteria search failed');
      setProjects([]);
      setMatchedUnitTotal(0);
      setCriteriaUnitsByProjectId({});
      setCriteriaActive(true);
    } finally {
      setCriteriaLoading(false);
    }
  }, [applyFocus, criteriaFilters]);

  const handleCriteriaClear = useCallback(() => {
    setCriteriaFilters({ priceMin: '1' });
    setCriteriaActive(false);
    setCriteriaError(null);
    setMatchedUnitTotal(0);
    setMatchedUnits([]);
    setMatchedUnitsError(null);
    setCriteriaUnitsByProjectId({});
    setProjects(allProjects);
    setSelectedId(null);
  }, [allProjects]);

  const loadMatchedUnitsForProject = useCallback(
    async (project: MasterToolProjectResult) => {
      if (!project.id || !project.name) return;
      setMatchedUnitsError(null);

      const cached = criteriaUnitsByProjectId[project.id];
      if (cached) {
        setMatchedUnits(cached);
        setMatchedUnitsLoading(false);
        return;
      }

      setMatchedUnits([]);
      setMatchedUnitsLoading(true);
      try {
        const collected = await fetchMatchedUnits([project.name.trim()]);
        setMatchedUnits(collected);
        setCriteriaUnitsByProjectId((prev) => ({
          ...prev,
          [project.id!]: collected,
        }));
      } catch (err) {
        setMatchedUnitsError(err instanceof Error ? err.message : 'Failed to load matched units');
        setMatchedUnits([]);
      } finally {
        setMatchedUnitsLoading(false);
      }
    },
    [criteriaUnitsByProjectId, fetchMatchedUnits],
  );

  const handlePickProjectResult = useCallback(
    (entry: MapProject) => {
      const label = (entry.project.name || 'Project').trim();
      turnOffSuperRatio();
      setNearbyPromoActive(false);
      skipSearchAutocompleteRef.current = true;
      setSearchQuery(label);
      setPlaceResults([]);
      setSearchOpen(false);
      setSelectedId(entry.id);
      applyFocus({ lat: entry.lat, lng: entry.lng, label });
      if (criteriaActive) {
        void loadMatchedUnitsForProject(entry.project);
      } else {
        setMatchedUnits([]);
        setMatchedUnitsError(null);
        setMatchedUnitsLoading(false);
      }
    },
    [applyFocus, criteriaActive, loadMatchedUnitsForProject, turnOffSuperRatio],
  );

  const promoUnitsByProject = useMemo(() => {
    const map = new Map<string, PricePromoUnit[]>();
    for (const group of pricePromoGroups) map.set(group.projectId, group.units);
    return map;
  }, [pricePromoGroups]);

  const thumbUnitsByProject = useMemo(() => {
    const map = new Map<string, ThumbsupUnit[]>();
    for (const group of thumbsupGroups) map.set(group.projectId, group.units);
    return map;
  }, [thumbsupGroups]);

  /** Within criteria search hits, which projects have promo / thumbs matched units. */
  const criteriaDealProjectMeta = useMemo(() => {
    const promoIds = new Set<string>();
    const thumbIds = new Set<string>();
    const promoCounts = new Map<string, number>();
    const thumbCounts = new Map<string, number>();
    for (const [projectId, units] of Object.entries(criteriaUnitsByProjectId)) {
      let promoCount = 0;
      let thumbCount = 0;
      for (const unit of units) {
        if (promoUnitIds.has(unit.id)) promoCount += 1;
        if (thumbsupUnitIds.has(unit.id)) thumbCount += 1;
      }
      if (promoCount > 0) {
        promoIds.add(projectId);
        promoCounts.set(projectId, promoCount);
      }
      if (thumbCount > 0) {
        thumbIds.add(projectId);
        thumbCounts.set(projectId, thumbCount);
      }
    }
    return { promoIds, thumbIds, promoCounts, thumbCounts };
  }, [criteriaUnitsByProjectId, promoUnitIds, thumbsupUnitIds]);

  /** Candidate projects for deal AND that still need a criteria-unit fetch. */
  const dealAndCandidates = useMemo(() => {
    if (!criteriaActive || dealFilter === 'none') return [];
    const dealMap = dealFilter === 'promo' ? promoUnitsByProject : thumbUnitsByProject;
    return projects.filter((p) => Boolean(p.id && p.name && dealMap.has(p.id!)));
  }, [criteriaActive, dealFilter, projects, promoUnitsByProject, thumbUnitsByProject]);

  const dealAndReady = useMemo(() => {
    if (!criteriaActive || dealFilter === 'none') return true;
    return dealAndCandidates.every((p) => p.id != null && p.id in criteriaUnitsByProjectId);
  }, [criteriaActive, dealFilter, dealAndCandidates, criteriaUnitsByProjectId]);

  const criteriaUnitsByProjectIdRef = useRef(criteriaUnitsByProjectId);
  criteriaUnitsByProjectIdRef.current = criteriaUnitsByProjectId;

  // When Price Promo / Thumbsup is on after a criteria search, fetch units only for
  // the small overlapping project set (not every matched unit across the island).
  useEffect(() => {
    if (!criteriaActive || dealFilter === 'none') {
      setDealMetaLoading(false);
      return;
    }

    const dealMap = dealFilter === 'promo' ? promoUnitsByProject : thumbUnitsByProject;
    const candidates = projects.filter((p) => Boolean(p.id && p.name && dealMap.has(p.id!)));
    const missing = candidates.filter(
      (p) => p.id != null && !(p.id in criteriaUnitsByProjectIdRef.current),
    );
    if (missing.length === 0) {
      setDealMetaLoading(false);
      return;
    }

    let cancelled = false;
    setDealMetaLoading(true);
    void (async () => {
      try {
        const names = missing.map((p) => (p.name ?? '').trim()).filter(Boolean);
        const collected = await fetchMatchedUnits(names);
        if (cancelled) return;
        const grouped = groupUnitsByProjectId(collected, missing);
        setCriteriaUnitsByProjectId((prev) => {
          const next = { ...prev };
          for (const project of missing) {
            if (!project.id) continue;
            next[project.id] = grouped[project.id] ?? [];
          }
          return next;
        });
      } catch (err) {
        console.error('Map Research deal AND enrichment failed:', err);
        if (!cancelled) {
          setCriteriaUnitsByProjectId((prev) => {
            const next = { ...prev };
            for (const project of missing) {
              if (project.id && !(project.id in next)) next[project.id] = [];
            }
            return next;
          });
        }
      } finally {
        if (!cancelled) setDealMetaLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    criteriaActive,
    dealFilter,
    projects,
    promoUnitsByProject,
    thumbUnitsByProject,
    fetchMatchedUnits,
    groupUnitsByProjectId,
  ]);

  const mapProjects = useMemo<MapProject[]>(() => {
    return projects
      .filter((project) => !isGlsProjectName(project.name))
      .map((project) => {
        const lat = Number(project.mapLatitude);
        const lng = Number(project.mapLongitude);
        if (!project.id || !Number.isFinite(lat) || !Number.isFinite(lng) || lat === 0 || lng === 0) return null;
        return { project, id: project.id, lat, lng };
      })
      .filter((entry): entry is MapProject => entry !== null);
  }, [projects]);

  const visibleProjects = useMemo(() => {
    let list: MapProject[];
    if (dealFilter === 'promo') {
      if (criteriaActive) {
        if (!dealAndReady) {
          // Optimistic: show criteria ∩ promo projects while unit-level AND loads.
          list = mapProjects.filter((p) => promoUnitsByProject.has(p.id));
        } else {
          list = mapProjects.filter((p) => criteriaDealProjectMeta.promoIds.has(p.id));
        }
      } else {
        list = mapProjects.filter((p) => promoUnitsByProject.has(p.id));
      }
    } else if (dealFilter === 'thumbsup') {
      if (criteriaActive) {
        if (!dealAndReady) {
          list = mapProjects.filter((p) => thumbUnitsByProject.has(p.id));
        } else {
          list = mapProjects.filter((p) => criteriaDealProjectMeta.thumbIds.has(p.id));
        }
      } else {
        list = mapProjects.filter((p) => thumbUnitsByProject.has(p.id));
      }
    } else {
      list = mapProjects;
    }

    // Nearby Price Promo: keep only promos inside the user's location radius.
    if (nearbyPromoActive && focus && dealFilter === 'promo') {
      list = list.filter(
        (p) => haversineKm(focus.lat, focus.lng, p.lat, p.lng) <= radiusKm,
      );
    }
    return list;
  }, [
    mapProjects,
    dealFilter,
    criteriaActive,
    dealAndReady,
    criteriaDealProjectMeta,
    promoUnitsByProject,
    thumbUnitsByProject,
    nearbyPromoActive,
    focus,
    radiusKm,
  ]);

  const getMatchBadgeCount = useCallback(
    (projectId: string, matchedUnitCount: number | null | undefined): number | null => {
      if (!criteriaActive) return null;
      if (dealFilter === 'promo') {
        if (!dealAndReady) return matchedUnitCount ?? 0;
        return criteriaDealProjectMeta.promoCounts.get(projectId) ?? 0;
      }
      if (dealFilter === 'thumbsup') {
        if (!dealAndReady) return matchedUnitCount ?? 0;
        return criteriaDealProjectMeta.thumbCounts.get(projectId) ?? 0;
      }
      return matchedUnitCount ?? 0;
    },
    [criteriaActive, dealFilter, dealAndReady, criteriaDealProjectMeta],
  );

  const projectsInRadius = useMemo(() => {
    if (!focus) return null;
    return visibleProjects.filter((p) => haversineKm(focus.lat, focus.lng, p.lat, p.lng) <= radiusKm).length;
  }, [focus, radiusKm, visibleProjects]);

  const selected = useMemo(
    () =>
      selectedId
        ? mapProjects.find((p) => p.id === selectedId) ??
          searchableMapProjects.find((p) => p.id === selectedId) ??
          null
        : null,
    [selectedId, mapProjects, searchableMapProjects],
  );

  const getMarkerIcon = useCallback((
    kind: MarkerKind,
    isSelected: boolean,
    dimmed: boolean,
    matchCount?: number | null,
    inCompareCart = false,
  ): L.DivIcon => {
    const countKey = matchCount != null && matchCount > 0 ? String(matchCount) : '';
    const key = `${kind}|${isSelected ? 's' : ''}|${dimmed ? 'd' : ''}|${countKey}|${inCompareCart ? 'c' : ''}`;
    const cached = iconCacheRef.current.get(key);
    if (cached) return cached;
    const palette = MARKER_PALETTE[kind];
    const size = isSelected ? 36 : 28;
    const iconPx = isSelected ? 18 : 15;
    const pulse = isSelected || kind !== 'project' || Boolean(countKey);
    const badgeH = countKey ? 20 : 0;
    const boxH = size + badgeH + (countKey ? 4 : 0);
    const countBadge = countKey
      ? `<span class="mr-match-count" title="${countKey} matched units">${countKey}</span>`
      : '';
    const blinkClass = isSelected ? ' mr-marker-search-blink' : inCompareCart ? ' mr-marker-compare' : '';
    const html = `
      <div class="mr-marker${dimmed && !isSelected ? ' mr-marker-dim' : ''}${blinkClass}" style="width:${Math.max(size, 36)}px;height:${boxH}px;">
        ${countBadge}
        <div class="mr-marker-icon" style="width:${size}px;height:${size}px;">
          ${pulse && (!dimmed || isSelected) ? `<span class="mr-marker-ring" style="box-shadow:0 0 0 2px ${isSelected ? 'rgba(34, 211, 238, 0.9)' : palette.glow};"></span>` : ''}
          <span class="mr-marker-core" style="width:${size}px;height:${size}px;background:${palette.core};box-shadow:0 0 ${isSelected ? 14 : 9}px 2px ${isSelected ? 'rgba(34, 211, 238, 0.95)' : palette.glow};${isSelected ? 'border:2px solid #fff;' : 'border:1.5px solid rgba(255,255,255,0.85);'}">
            <svg width="${iconPx}" height="${iconPx}" viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="${palette.icon}"/>
            </svg>
          </span>
          ${inCompareCart && !isSelected ? '<span class="mr-compare-flag"></span>' : ''}
        </div>
      </div>`;
    const icon = L.divIcon({
      className: 'mr-marker-wrap',
      html,
      iconSize: [Math.max(size, 36), boxH],
      iconAnchor: [Math.max(size, 36) / 2, boxH - size / 2],
    });
    iconCacheRef.current.set(key, icon);
    return icon;
  }, []);

  const markerKind = dealFilterToMarkerKind(dealFilter);

  const focusIcon = useMemo(
    () =>
      L.divIcon({
        className: 'mr-marker-wrap',
        html: `
          <div class="mr-marker" style="width:34px;height:34px;">
            <span class="mr-marker-ring" style="box-shadow:0 0 0 2px rgba(167,139,250,0.8);"></span>
            <span class="mr-marker-core" style="width:14px;height:14px;background:linear-gradient(135deg,#ddd6fe,#8b5cf6);box-shadow:0 0 12px 3px rgba(139,92,246,0.75);border:2px solid #fff;"></span>
          </div>`,
        iconSize: [34, 34],
        iconAnchor: [17, 17],
      }),
    [],
  );

  const flashPlans = flashPicks
    .map((id) => MASTER_PLANS.find((mp) => mp.id === id))
    .filter((mp): mp is (typeof MASTER_PLANS)[number] => Boolean(mp));

  const masterPlanActive = flashRunning ? flashPlans.length === 2 : Boolean(activeMasterPlan);

  /** Unit Criteria / Price Promo / Thumbsup — mutually exclusive with Super Ratio. */
  const researchToolsActive = criteriaActive || dealFilter !== 'none';
  /** Mobile clearance above the tool dock (+ YaoYao bar when Price Promo is available). */
  const mobileBottomChrome = canFilterPricePromo ? 122 : 68;
  const researchToolsDisabled = superRatioActive;
  // No location needed — Super Ratio opens island-wide and narrows once one is set.
  const superRatioDisabled = !canUseSuperRatio || researchToolsActive;

  const deactivateSuperRatio = turnOffSuperRatio;

  const clearResearchToolsForSuperRatio = useCallback(
    (options?: { keepSelection?: boolean }) => {
      setDealFilter('none');
      setNearbyPromoActive(false);
      setCriteriaAnchor(null);
      setCriteriaFilters({ priceMin: '1' });
      setCriteriaActive(false);
      setCriteriaError(null);
      setMatchedUnitTotal(0);
      setMatchedUnits([]);
      setMatchedUnitsError(null);
      setCriteriaUnitsByProjectId({});
      setProjects(allProjects);
      if (!options?.keepSelection) setSelectedId(null);
    },
    [allProjects],
  );

  // Shake (YaoYao) — only while the user has enabled it (and granted motion on iOS).
  // Listener identity is stable: lastShakeAt lives in a ref so iOS (fast GPS +
  // high-rate motion) can't re-attach a fresh listener mid-shake and toggle twice.
  useEffect(() => {
    if (!canFilterPricePromo || !yaoyaoEnabled || typeof window === 'undefined') return;

    let lastX = 0;
    let lastY = 0;
    let lastZ = 0;
    let primed = false;
    let hitTimes: number[] = [];

    const onMotion = (event: DeviceMotionEvent) => {
      if (!yaoyaoEnabledRef.current) return;
      if (superRatioActiveRef.current) return;
      // Don't toggle while geolocation / radar overlay is running.
      if (nearbyPromoLocatingRef.current) {
        hitTimes = [];
        return;
      }

      const now = Date.now();
      const inCooldown = now - lastShakeAtRef.current < SHAKE_COOLDOWN_MS;

      // Prefer linear acceleration — ignores gravity/tilt from lifting the phone.
      const linear = event.acceleration;
      const hasLinear =
        linear != null &&
        linear.x != null &&
        linear.y != null &&
        linear.z != null &&
        !(linear.x === 0 && linear.y === 0 && linear.z === 0);

      if (!hasLinear) {
        const acc = event.accelerationIncludingGravity;
        if (!acc || acc.x == null || acc.y == null || acc.z == null) return;
        // Always refresh baseline during cooldown so unlock doesn't see a huge delta.
        if (!primed || inCooldown) {
          lastX = acc.x;
          lastY = acc.y;
          lastZ = acc.z;
          primed = true;
          hitTimes = [];
          return;
        }
        const delta = Math.abs(acc.x - lastX) + Math.abs(acc.y - lastY) + Math.abs(acc.z - lastZ);
        lastX = acc.x;
        lastY = acc.y;
        lastZ = acc.z;
        if (delta < SHAKE_THRESHOLD_WITH_GRAVITY) return;
      } else {
        if (inCooldown) {
          hitTimes = [];
          return;
        }
        const magnitude = Math.sqrt(linear.x! ** 2 + linear.y! ** 2 + linear.z! ** 2);
        if (magnitude < SHAKE_THRESHOLD) return;
      }

      hitTimes = hitTimes.filter((t) => now - t <= SHAKE_HIT_WINDOW_MS);
      hitTimes.push(now);
      if (hitTimes.length < SHAKE_REQUIRED_HITS) return;

      // Lock out immediately so the rest of this shake can't reverse the toggle.
      hitTimes = [];
      lastShakeAtRef.current = now;
      toggleNearbyPromoRef.current();
    };

    window.addEventListener('devicemotion', onMotion);
    return () => window.removeEventListener('devicemotion', onMotion);
  }, [canFilterPricePromo, yaoyaoEnabled]);

  const activateSuperRatio = useCallback(() => {
    if (superRatioDisabled && !superRatioActive) return;
    // Keep any open project console so marker detail can nest under it.
    clearResearchToolsForSuperRatio({ keepSelection: true });
    // No location picked — open on the whole island until one is keyed in.
    if (!focus) {
      setSuperRatioNationwide(true);
      setMinRadiusKm(0);
      mapRef.current?.flyToBounds(SG_MAP_BOUNDS, { padding: [40, 40], duration: 0.8 });
    }
    setSuperRatioActive(true);
  }, [superRatioDisabled, superRatioActive, clearResearchToolsForSuperRatio, focus]);

  /** From a project popup: focus that project, show radius, open Super Ratio — keep the popup open. */
  const launchSuperRatioAtProject = useCallback(
    (project: MasterToolProjectResult) => {
      if (!canUseSuperRatio || !project.id) return;
      const lat = Number(project.mapLatitude);
      const lng = Number(project.mapLongitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat === 0 || lng === 0) return;

      clearResearchToolsForSuperRatio({ keepSelection: true });
      setSelectedId(project.id);
      const label = (project.name || 'Selected project').trim();
      skipSearchAutocompleteRef.current = true;
      setSearchQuery(label);
      setPlaceResults([]);
      setSearchOpen(false);
      setSearchLoading(false);
      const scoped = scopeIslandScanToFocus();
      setFocus({ lat, lng, label });
      setLocationError(null);
      fitToRadius(lat, lng, scoped ? SUPER_RATIO_FOCUS_RADIUS_KM : radiusKm);
      setSuperRatioActive(true);
    },
    [canUseSuperRatio, clearResearchToolsForSuperRatio, fitToRadius, radiusKm, scopeIslandScanToFocus],
  );

  const controlButtonSx = (active: boolean, disabled = false) => ({
    ...glassPanelSx,
    width: 44,
    height: 44,
    borderRadius: '12px',
    color: active ? '#22d3ee' : 'rgba(238, 246, 250, 0.85)',
    borderColor: active ? 'rgba(34, 211, 238, 0.7)' : 'rgba(34, 211, 238, 0.28)',
    opacity: disabled ? 0.4 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    '&:hover': { bgcolor: disabled ? glassPanelSx.bgcolor : 'rgba(20, 36, 56, 0.92)' },
  });

  const dealChipSx = (active: boolean, color: string, disabled = false) => ({
    ...glassPanelSx,
    height: 34,
    px: 0.5,
    fontWeight: 700,
    fontSize: 11.5,
    letterSpacing: '0.02em',
    color: active ? '#04121d' : color,
    background: active ? color : glassPanelSx.bgcolor,
    borderColor: active ? color : 'rgba(34, 211, 238, 0.28)',
    opacity: disabled ? 0.4 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    '& .MuiChip-icon': { color: active ? '#04121d' : color },
    '&:hover': {
      background: disabled
        ? active
          ? color
          : glassPanelSx.bgcolor
        : active
          ? color
          : 'rgba(20, 36, 56, 0.92)',
    },
  });

  return (
    <Box
      sx={{
        position: 'relative',
        height: { xs: 'calc(100vh - 300px)', md: 'calc(100vh - 270px)' },
        minHeight: 540,
        borderRadius: '16px',
        overflow: 'hidden',
        border: '1px solid rgba(34, 211, 238, 0.25)',
        boxShadow: '0 12px 48px rgba(var(--color-shadow-rgb), 0.4), 0 0 24px rgba(34, 211, 238, 0.07)',
        // Popup / tooltip / marker chrome shared with ProjectPopupCard.
        '& .leaflet-container': { fontFamily: 'inherit', bgcolor: basemap.dark ? '#0a1626' : '#dbe6ee' },
      }}
    >
      <style>{`
        .mr-marker-wrap { background: transparent; border: none; }
        .mr-marker {
          position: relative; display: flex; flex-direction: column; align-items: center;
          justify-content: flex-end; gap: 4px;
        }
        .mr-marker-icon { position: relative; display: flex; align-items: center; justify-content: center; }
        .mr-marker-core {
          border-radius: 8px; display: flex; align-items: center; justify-content: center;
          transition: transform 0.15s ease;
        }
        .mr-marker:hover .mr-marker-core { transform: scale(1.18); }
        .mr-marker-dim { opacity: 0.28; filter: grayscale(0.7); }
        .mr-marker-ring {
          position: absolute; inset: -4px; border-radius: 12px;
          animation: mrPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .mr-match-count {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 22px; height: 20px; padding: 0 6px;
          border-radius: 999px; font-weight: 800; font-size: 11px; line-height: 1;
          color: #04121d; background: linear-gradient(135deg, #67e8f9 0%, #22d3ee 100%);
          border: 1.5px solid rgba(255,255,255,0.9);
          box-shadow: 0 0 12px rgba(34, 211, 238, 0.65), 0 2px 6px rgba(2, 8, 20, 0.45);
          pointer-events: none;
        }
        @keyframes mrPulse {
          0% { transform: scale(0.5); opacity: 0.9; }
          70% { transform: scale(1.15); opacity: 0; }
          100% { transform: scale(1.15); opacity: 0; }
        }
        .mr-tooltip {
          background: rgba(10, 20, 34, 0.92) !important;
          border: 1px solid rgba(34, 211, 238, 0.4) !important;
          border-radius: 8px !important;
          color: #eef6fa !important;
          font-weight: 700; font-size: 11.5px;
          padding: 4px 9px !important;
          box-shadow: 0 6px 20px rgba(2, 8, 20, 0.55) !important;
        }
        .mr-tooltip::before { display: none; }
        .mr-scanline {
          position: absolute; top: 0; left: 0; right: 0; height: 2px; z-index: 4;
          background: linear-gradient(90deg, transparent 0%, #22d3ee 50%, transparent 100%);
          background-size: 60% 100%; background-repeat: no-repeat;
          animation: mrScan 3.2s ease-in-out infinite;
        }
        @keyframes mrScan {
          0% { background-position: -60% 0; }
          50% { background-position: 160% 0; }
          100% { background-position: -60% 0; }
        }
        .mr-pulse-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #22d3ee;
          box-shadow: 0 0 8px 2px rgba(34, 211, 238, 0.7);
          animation: mrDot 1.6s ease-in-out infinite;
        }
        @keyframes mrDot { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
        .mr-progress-fill { animation: mrGrow 0.9s ease-out; transform-origin: left; }
        @keyframes mrGrow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        /* Horizontal drift and vertical toss run on separate layers so the chip
           follows a gravity arc: eased-out sideways, up then accelerating down. */
        @keyframes mrThrowX {
          from { transform: translateX(0); }
          to { transform: translateX(var(--mr-throw-dx)); }
        }
        @keyframes mrThrowY {
          0% {
            transform: translateY(0);
            animation-timing-function: cubic-bezier(0.12, 0.72, 0.3, 1);
          }
          40% {
            transform: translateY(calc(var(--mr-throw-dy) * 0.34 - var(--mr-throw-lift)));
            animation-timing-function: cubic-bezier(0.5, 0.02, 0.72, 0.45);
          }
          100% { transform: translateY(var(--mr-throw-dy)); }
        }
        @keyframes mrThrowChip {
          0% { transform: scale(0.55) rotate(-8deg); opacity: 0; }
          14% { transform: scale(1.06) rotate(-3deg); opacity: 1; }
          72% { transform: scale(0.88) rotate(7deg); opacity: 1; }
          100% { transform: scale(0.3) rotate(14deg); opacity: 0; }
        }
        @keyframes mrThrowTrail {
          0% { opacity: 0; transform: scale(0.4); }
          25% { opacity: 0.55; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.5); }
        }
        @keyframes mrCompareFabPulse {
          0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(167, 139, 250, 0.6); }
          30% { transform: scale(1.2); box-shadow: 0 0 0 9px rgba(167, 139, 250, 0.14); }
          58% { transform: scale(0.93); box-shadow: 0 0 0 16px rgba(167, 139, 250, 0); }
          78% { transform: scale(1.06); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(167, 139, 250, 0); }
        }
        @keyframes mrPhoneVibrate {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          20% { transform: translate(-1.5px, 1px) rotate(-8deg); }
          40% { transform: translate(1.5px, -1px) rotate(8deg); }
          60% { transform: translate(-1px, -1px) rotate(-5deg); }
          80% { transform: translate(1px, 1px) rotate(5deg); }
        }
        /* Compare-cart members blink so they stay findable on the map. */
        .mr-marker-compare .mr-marker-core {
          animation: mrCompareBlink 1.5s ease-in-out infinite;
        }
        .mr-marker-compare .mr-compare-flag {
          position: absolute; top: -6px; right: -6px;
          width: 14px; height: 14px; border-radius: 50%;
          background: linear-gradient(135deg, #ddd6fe 0%, #8b5cf6 100%);
          border: 1.5px solid rgba(255, 255, 255, 0.9);
          box-shadow: 0 0 8px rgba(167, 139, 250, 0.8);
          animation: mrCompareBlink 1.5s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes mrCompareBlink {
          0%, 100% { opacity: 1; box-shadow: 0 0 16px 4px rgba(167, 139, 250, 0.9); }
          50% { opacity: 0.35; box-shadow: 0 0 3px 1px rgba(167, 139, 250, 0.2); }
        }
        /* Search-selected project — cyan blink to draw the eye after a pick. */
        .mr-marker-search-blink .mr-marker-core {
          animation: mrSearchBlink 0.7s ease-in-out infinite;
        }
        .mr-marker-search-blink .mr-marker-ring {
          animation: mrPulse 1s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes mrSearchBlink {
          0%, 100% { opacity: 1; box-shadow: 0 0 22px 6px rgba(34, 211, 238, 1); transform: scale(1); }
          50% { opacity: 0.4; box-shadow: 0 0 4px 1px rgba(34, 211, 238, 0.25); transform: scale(0.92); }
        }
      `}</style>

      <MapContainer
        center={SG_CENTER}
        zoom={12}
        minZoom={SG_MIN_ZOOM}
        maxZoom={SG_MAX_ZOOM}
        maxBounds={SG_MAX_BOUNDS}
        maxBoundsViscosity={1.0}
        zoomControl={false}
        style={{ height: '100%', width: '100%' }}
      >
        <MapBridge
          mapRef={mapRef}
          onBareClick={() => {
            // Super Ratio session: never dismiss the project popup / mode on a missed map click.
            if (superRatioActiveRef.current) return;
            setSelectedId(null);
            setMatchedUnits([]);
            setMatchedUnitsError(null);
          }}
        />
        <TileLayer key={`base-${basemap.id}`} url={basemap.url} attribution={basemap.attribution} zIndex={1} />

        {/* Single master plan overlay (flash mode takes over when running) */}
        {!flashRunning && activeMasterPlan && (
          <TileLayer
            key={`mp-${activeMasterPlan.id}`}
            url={activeMasterPlan.url}
            attribution={URA_ATTRIBUTION}
            opacity={masterPlanOpacity}
            zIndex={5}
          />
        )}
        {flashRunning &&
          flashPlans.map((mp, idx) => (
            <TileLayer
              key={`flash-${mp.id}`}
              url={mp.url}
              attribution={URA_ATTRIBUTION}
              opacity={idx === flashIndex ? masterPlanOpacity : 0}
              zIndex={5}
            />
          ))}

        {/* Search focus + adjustable radius — a whole-island scan keeps the
            focus pin but has no ring to draw. */}
        {focus && (
          <>
            <Marker position={[focus.lat, focus.lng]} icon={focusIcon}>
              <LeafletTooltip className="mr-tooltip" direction="top" offset={[0, -14]}>
                {focus.label}
              </LeafletTooltip>
            </Marker>
            {!(superRatioActive && superRatioNationwide) && (
              <Circle
                center={[focus.lat, focus.lng]}
                radius={radiusKm * 1000}
                interactive={false}
                pathOptions={{
                  color: '#8b5cf6',
                  weight: 2,
                  dashArray: '6 6',
                  fillColor: '#8b5cf6',
                  // A ring scan leaves the middle unfilled so the band reads clearly.
                  fillOpacity: superRatioMinRadiusMeters > 0 ? 0 : 0.07,
                }}
              />
            )}
            {/* Inner edge — dashed and lightly scrimmed, so the excluded core
                reads as a cut-out rather than a second target ring. */}
            {superRatioMinRadiusMeters > 0 && !superRatioNationwide && (
              <Circle
                center={[focus.lat, focus.lng]}
                radius={superRatioMinRadiusMeters}
                interactive={false}
                pathOptions={{
                  color: '#8b5cf6',
                  weight: 1.5,
                  dashArray: '5 5',
                  fillColor: '#e9d5ff',
                  fillOpacity: 0.18,
                }}
              />
            )}
          </>
        )}

        {/* Project markers — hidden while Super Ratio layers own the map */}
        {!superRatioActive &&
          (() => {
            const visibleIds = new Set(visibleProjects.map((p) => p.id));
            const markers =
              selected && !visibleIds.has(selected.id)
                ? [...visibleProjects, selected]
                : visibleProjects;
            return markers.map(({ project, id, lat, lng }) => {
              const dimmed = focus ? haversineKm(focus.lat, focus.lng, lat, lng) > radiusKm : false;
              const matchCount = getMatchBadgeCount(id, project.matchedUnitCount);
              const isSelected = selectedId === id;
              return (
                <Marker
                  key={id}
                  position={[lat, lng]}
                  zIndexOffset={isSelected ? 1000 : 0}
                  icon={getMarkerIcon(
                    markerKind,
                    isSelected,
                    dimmed && !isSelected,
                    matchCount,
                    compareCartIds.has(id),
                  )}
                  eventHandlers={{
                    click: () => {
                      setSelectedId(id);
                      if (criteriaActive) {
                        void loadMatchedUnitsForProject(project);
                      } else {
                        setMatchedUnits([]);
                        setMatchedUnitsError(null);
                        setMatchedUnitsLoading(false);
                      }
                    },
                  }}
                >
                  <LeafletTooltip
                    className="mr-tooltip"
                    direction="top"
                    offset={[0, criteriaActive ? -28 : -14]}
                  >
                    {criteriaActive && matchCount != null && matchCount > 0
                      ? `${project.name} · ${matchCount} match${matchCount === 1 ? '' : 'es'}`
                      : project.name}
                  </LeafletTooltip>
                </Marker>
              );
            });
          })()}

        {superRatioActive && superRatioFocus && (
          <DemandSupplyMapMarkers
            latitude={superRatioFocus.lat}
            longitude={superRatioFocus.lng}
            data={superRatioData}
            radius={superRatioRadiusMeters}
            minRadius={superRatioMinRadiusMeters}
            selectedTypes={selectedDemandSupplyTypes}
            showFocusMarker={false}
            showRadiusCircle={false}
            onSelect={setSuperRatioSelection}
          />
        )}
      </MapContainer>

      {/* Super Ratio marker detail is shown in the bottom-right popup slot (same as ProjectPopupCard). */}

      {/* ── Search panel (top-left) ─────────────────────────────────── */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 1000,
          // Mobile: only leave room for the slim chrome column (tools move to bottom dock).
          width: {
            xs: 'calc(100% - 64px)',
            sm: 'calc(100% - 72px)',
            md: superRatioActive ? 400 : 320,
          },
          // Stay inside the map frame so chart axis labels aren't clipped by overflow:hidden.
          maxHeight: { xs: 'calc(100% - 88px)', md: 'calc(100% - 24px)' },
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 0.5,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Paper sx={{ ...glassPanelSx, p: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search project, location, MRT…"
              value={searchQuery}
              onChange={(e) => {
                skipSearchAutocompleteRef.current = false;
                setSearchQuery(e.target.value);
              }}
              onFocus={() => {
                if (skipSearchAutocompleteRef.current) return;
                if (hasSearchSuggestions) setSearchOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (projectSearchMatches.length > 0) {
                    handlePickProjectResult(projectSearchMatches[0]);
                  } else if (placeResults.length > 0) {
                    handlePickSearchResult(placeResults[0]);
                  }
                }
                if (e.key === 'Escape') setSearchOpen(false);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#22d3ee' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {searchLoading ? (
                      <CircularProgress size={14} sx={{ color: '#22d3ee' }} />
                    ) : searchQuery ? (
                      <IconButton size="small" onClick={clearFocus} sx={{ color: 'rgba(238,246,250,0.6)' }}>
                        <CloseIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    ) : null}
                  </InputAdornment>
                ),
                sx: {
                  color: '#eef6fa',
                  fontSize: 13,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(34, 211, 238, 0.25)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(34, 211, 238, 0.5)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#22d3ee' },
                },
              }}
            />
            <Tooltip title="Use current location">
              <IconButton
                onClick={handleUseCurrentLocation}
                disabled={locating}
                sx={{ color: '#22d3ee', flexShrink: 0 }}
              >
                {locating ? <CircularProgress size={18} sx={{ color: '#22d3ee' }} /> : <MyLocationIcon sx={{ fontSize: 20 }} />}
              </IconButton>
            </Tooltip>
          </Box>

          {searchOpen && hasSearchSuggestions && (
            <List dense disablePadding sx={{ mt: 0.5, maxHeight: 280, overflowY: 'auto' }}>
              {projectSearchMatches.length > 0 && (
                <>
                  <Box
                    sx={{
                      ...microLabelSx,
                      px: 1.25,
                      pt: 0.75,
                      pb: 0.35,
                      color: '#22d3ee',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <ApartmentIcon sx={{ fontSize: 12 }} />
                    Projects
                  </Box>
                  {projectSearchMatches.map((entry) => (
                    <ListItemButton
                      key={`project-${entry.id}`}
                      onClick={() => handlePickProjectResult(entry)}
                      sx={{ borderRadius: '8px', '&:hover': { bgcolor: 'rgba(34, 211, 238, 0.12)' } }}
                    >
                      <ListItemText
                        primary={entry.project.name || 'Unnamed'}
                        primaryTypographyProps={{ sx: { fontSize: 12.5, color: '#eef6fa', fontWeight: 600 } }}
                        secondary={
                          [entry.project.district, entry.project.address].filter(Boolean).join(' · ') ||
                          undefined
                        }
                        secondaryTypographyProps={{ sx: { fontSize: 10.5, color: 'rgba(196, 220, 233, 0.6)' } }}
                      />
                    </ListItemButton>
                  ))}
                </>
              )}

              {placeResults.length > 0 && (
                <>
                  <Box
                    sx={{
                      ...microLabelSx,
                      px: 1.25,
                      pt: projectSearchMatches.length > 0 ? 1 : 0.75,
                      pb: 0.35,
                      color: 'rgba(148, 197, 222, 0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      borderTop:
                        projectSearchMatches.length > 0 ? '1px solid rgba(34, 211, 238, 0.15)' : 'none',
                      mt: projectSearchMatches.length > 0 ? 0.5 : 0,
                    }}
                  >
                    <PlaceIcon sx={{ fontSize: 12 }} />
                    Places in Singapore
                  </Box>
                  {placeResults.map((result, index) => (
                    <ListItemButton
                      key={`place-${result.SEARCHVAL}-${index}`}
                      onClick={() => handlePickSearchResult(result)}
                      sx={{ borderRadius: '8px', '&:hover': { bgcolor: 'rgba(34, 211, 238, 0.12)' } }}
                    >
                      <ListItemText
                        primary={formatOnemapLocationLabel(result)}
                        primaryTypographyProps={{ sx: { fontSize: 12.5, color: '#eef6fa', fontWeight: 600 } }}
                        secondary={result.ADDRESS && result.ADDRESS !== 'NIL' ? result.ADDRESS : undefined}
                        secondaryTypographyProps={{ sx: { fontSize: 10.5, color: 'rgba(196, 220, 233, 0.6)' } }}
                      />
                    </ListItemButton>
                  ))}
                </>
              )}
            </List>
          )}
        </Paper>

        {locationError && (
          <Alert
            severity="warning"
            onClose={() => setLocationError(null)}
            sx={{ mt: 1, py: 0, fontSize: 12, borderRadius: '10px' }}
          >
            {locationError}
          </Alert>
        )}
        {criteriaError && (
          <Alert
            severity="warning"
            onClose={() => setCriteriaError(null)}
            sx={{ mt: 1, py: 0, fontSize: 12, borderRadius: '10px' }}
          >
            {criteriaError}
          </Alert>
        )}

        {/* Radius scope panel — compact summary on mobile, full slider on md+ / when
            expanded. An island-wide scan has no location, but still owns the scope. */}
        {(focus || (superRatioActive && superRatioNationwide)) && (
          <Paper sx={{ ...glassPanelSx, mt: 1, px: { xs: 1.1, md: 1.5 }, py: { xs: 0.7, md: 1 } }}>
            {/* Mobile collapsed summary */}
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => setMobileRadiusExpanded((open) => !open)}
                sx={{
                  appearance: 'none',
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  border: 'none',
                  background: 'transparent',
                  color: 'inherit',
                  cursor: 'pointer',
                  p: 0,
                  textAlign: 'left',
                }}
              >
                <Typography sx={{ ...microLabelSx, color: '#a78bfa', flexShrink: 0 }}>
                  {scanRadiusLabel}
                </Typography>
                {!superRatioActive && (
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: 'rgba(222, 238, 246, 0.8)',
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box component="span" sx={{ color: '#22d3ee', fontWeight: 800 }}>
                      {projectsInRadius}
                    </Box>
                    {' '}in range
                  </Typography>
                )}
                {focus &&
                  (mobileRadiusExpanded ? (
                    <ExpandLessIcon sx={{ fontSize: 18, color: 'rgba(196, 220, 233, 0.7)', ml: 'auto' }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 18, color: 'rgba(196, 220, 233, 0.7)', ml: 'auto' }} />
                  ))}
              </Box>
              {focus && (
                <Button
                  size="small"
                  onClick={clearFocus}
                  sx={{ fontSize: 10.5, color: 'rgba(238,246,250,0.7)', minWidth: 0, px: 0.75 }}
                >
                  Clear
                </Button>
              )}
            </Box>

            {/* Drop both ends of the ring and scan every block and project
                island-wide. Super Ratio only — the other tools work off the disc. */}
            {superRatioActive && (
              <FormControlLabel
                sx={{
                  ml: 0,
                  mr: 0,
                  mb: -0.25,
                  display: { xs: mobileRadiusExpanded || !focus ? 'flex' : 'none', md: 'flex' },
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={superRatioNationwide}
                    disabled={superRatioLoading || !focus}
                    onChange={(e) => handleNationwideChange(e.target.checked)}
                    sx={{
                      p: 0.5,
                      color: 'rgba(148, 197, 222, 0.7)',
                      '&.Mui-checked': { color: '#8b5cf6' },
                    }}
                  />
                }
                label={
                  <Typography sx={{ ...microLabelSx, ml: 0.5 }}>Whole Singapore</Typography>
                }
              />
            )}

            {/* Desktop header */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography sx={microLabelSx}>Scan Radius</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#a78bfa' }}>
                {scanRadiusLabel}
              </Typography>
            </Box>

            {focus && (
              <Box sx={{ display: { xs: mobileRadiusExpanded ? 'block' : 'none', md: 'block' }, mt: { xs: 0.5, md: 0 } }}>
                {/* Super Ratio scans a ring — drag both ends for a from/to band such
                    as 2–5 km. The other research tools work off a plain disc, so the
                    slider stays single-thumb when Super Ratio is off. */}
                <Slider
                  // Remount on mode change — the thumb count changes with it.
                  key={superRatioActive ? 'ring' : 'disc'}
                  size="small"
                  disabled={superRatioActive && superRatioNationwide}
                  value={superRatioActive ? [minRadiusKm, radiusKm] : radiusKm}
                  min={superRatioActive ? 0 : RADIUS_MIN_KM}
                  max={RADIUS_MAX_KM}
                  step={RADIUS_STEP_KM}
                  disableSwap
                  onChange={(_e, value) => {
                    if (Array.isArray(value)) {
                      const [lo, hi] = value as number[];
                      // Keep at least one step of band so the ring never collapses.
                      if (hi - lo < RADIUS_STEP_KM) return;
                      setMinRadiusKm(lo);
                      setRadiusKm(Math.max(hi, RADIUS_MIN_KM));
                      return;
                    }
                    setRadiusKm(value as number);
                  }}
                  onChangeCommitted={(_e, value) => {
                    if (!focus) return;
                    const outer = Array.isArray(value) ? (value as number[])[1] : (value as number);
                    fitToRadius(focus.lat, focus.lng, outer);
                  }}
                  sx={{
                    color: '#8b5cf6',
                    '& .MuiSlider-thumb': { boxShadow: '0 0 8px rgba(139, 92, 246, 0.8)' },
                  }}
                />
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  {!superRatioActive ? (
                    <Typography sx={{ fontSize: 11.5, color: 'rgba(222, 238, 246, 0.85)' }}>
                      <Box component="span" sx={{ color: '#22d3ee', fontWeight: 800 }}>{projectsInRadius}</Box>
                      {' '}project{projectsInRadius === 1 ? '' : 's'} in range
                    </Typography>
                  ) : (
                    <Box />
                  )}
                  <Button size="small" onClick={clearFocus} sx={{ fontSize: 10.5, color: 'rgba(238,246,250,0.7)', minWidth: 0 }}>
                    Clear
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        )}

        {superRatioActive && superRatioFocus && (
          <Paper
            sx={{
              ...glassPanelSx,
              mt: 1,
              p: 1.25,
              pb: 2.5,
              // Parent column scrolls; don't clip the chart axes inside this paper.
              overflow: 'visible',
            }}
          >
            {superRatioError ? (
              <Alert severity="warning" sx={{ py: 0, fontSize: 12, borderRadius: '10px' }}>
                {superRatioError}
              </Alert>
            ) : (
              <DemandSupplyPanel
                data={superRatioData}
                loading={superRatioLoading}
                radius={superRatioRadiusMeters}
                minRadius={superRatioMinRadiusMeters}
                hideRadiusSlider
                chrome="mapResearch"
                selectedTypes={selectedDemandSupplyTypes}
                setSelectedTypes={setSelectedDemandSupplyTypes}
              />
            )}
          </Paper>
        )}
      </Box>

      {/* ── Control stack (top-right) ───────────────────────────────── */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 0.65, md: 1 },
          alignItems: 'flex-end',
        }}
      >
        {/* Map chrome — icon-only controls */}
        <Tooltip title="Refresh projects" placement="left">
          <span>
            <IconButton
              onClick={() => void fetchProjects()}
              disabled={projectsLoading}
              sx={{
                ...controlButtonSx(false, projectsLoading),
                width: { xs: 40, md: 44 },
                height: { xs: 40, md: 44 },
              }}
              aria-label="Refresh projects"
            >
              {projectsLoading ? (
                <CircularProgress size={18} sx={{ color: '#22d3ee' }} />
              ) : (
                <RefreshIcon sx={{ fontSize: { xs: 19, md: 21 } }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Basemap" placement="left">
          <IconButton
            onClick={(e) => setBasemapAnchor(e.currentTarget)}
            sx={{
              ...controlButtonSx(Boolean(basemapAnchor)),
              width: { xs: 40, md: 44 },
              height: { xs: 40, md: 44 },
            }}
          >
            <LayersIcon sx={{ fontSize: { xs: 19, md: 21 } }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="URA Master Plan" placement="left">
          <IconButton
            onClick={(e) => setMasterPlanAnchor(e.currentTarget)}
            sx={{
              ...controlButtonSx(masterPlanActive),
              width: { xs: 40, md: 44 },
              height: { xs: 40, md: 44 },
            }}
          >
            <MapIcon sx={{ fontSize: { xs: 19, md: 21 } }} />
          </IconButton>
        </Tooltip>

        {/* Research tools — desktop/tablet labeled chips; mobile uses bottom dock */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            gap: 0.75,
            alignItems: 'stretch',
            mt: 0.5,
            pt: 1,
            borderTop: '1px solid rgba(34, 211, 238, 0.2)',
            opacity: researchToolsDisabled ? 0.55 : 1,
            minWidth: 168,
          }}
        >
          <Tooltip
            title={
              researchToolsDisabled
                ? 'Turn off Super Ratio to use Unit Criteria'
                : 'Unit Criteria'
            }
            placement="left"
          >
            <span>
              <Chip
                clickable={!researchToolsDisabled}
                disabled={researchToolsDisabled}
                icon={<TuneIcon sx={{ fontSize: 15 }} />}
                label="Criteria"
                onClick={(e) => {
                  if (researchToolsDisabled) return;
                  setCriteriaAnchor(e.currentTarget);
                }}
                sx={{
                  ...dealChipSx(Boolean(criteriaAnchor) || criteriaActive, '#22d3ee', researchToolsDisabled),
                  width: '100%',
                  justifyContent: 'flex-start',
                }}
              />
            </span>
          </Tooltip>
          {canFilterPricePromo && (
            <Tooltip
              title={
                researchToolsDisabled
                  ? 'Turn off Super Ratio to use Price Promo'
                  : nearbyPromoActive
                    ? 'Exit nearby Price Promo'
                    : 'Price Promo'
              }
              placement="left"
            >
              <span>
                <Chip
                  clickable={!researchToolsDisabled}
                  disabled={researchToolsDisabled}
                  icon={<LocalOfferIcon sx={{ fontSize: 15 }} />}
                  label="Price Promo"
                  onClick={() => {
                    if (researchToolsDisabled) return;
                    if (nearbyPromoActive) {
                      exitNearbyPromo();
                      return;
                    }
                    setDealFilter((f) => (f === 'promo' ? 'none' : 'promo'));
                  }}
                  sx={{
                    ...dealChipSx(dealFilter === 'promo', '#fbbf24', researchToolsDisabled),
                    width: '100%',
                    justifyContent: 'flex-start',
                  }}
                />
              </span>
            </Tooltip>
          )}
          {canFilterPricePromo && (
            <Box
              sx={{
                mt: 0.25,
                px: 0.75,
                py: 0.35,
                borderRadius: '10px',
                border: '1px solid',
                borderColor: nearbyPromoActive
                  ? 'rgba(251, 191, 36, 0.7)'
                  : yaoyaoEnabled
                    ? 'rgba(251, 191, 36, 0.45)'
                    : 'rgba(34, 211, 238, 0.22)',
                bgcolor: nearbyPromoActive
                  ? 'rgba(251, 191, 36, 0.2)'
                  : yaoyaoEnabled
                    ? 'rgba(251, 191, 36, 0.1)'
                    : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 0.5,
                width: '100%',
                opacity: researchToolsDisabled ? 0.45 : 1,
              }}
            >
              <Tooltip
                title={
                  researchToolsDisabled
                    ? 'Turn off Super Ratio to use YaoYao'
                    : nearbyPromoActive
                      ? 'Exit nearby Price Promo'
                      : 'Scan Price Promos within 5 km of your location'
                }
                placement="left"
              >
                <Box
                  component="button"
                  type="button"
                  disabled={researchToolsDisabled || nearbyPromoLocating}
                  onClick={() => {
                    if (researchToolsDisabled || nearbyPromoLocating) return;
                    toggleNearbyPromo();
                  }}
                  sx={{
                    appearance: 'none',
                    border: 'none',
                    background: 'transparent',
                    p: 0,
                    m: 0,
                    cursor: researchToolsDisabled || nearbyPromoLocating ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    minWidth: 0,
                    flex: 1,
                    textAlign: 'left',
                    color: 'inherit',
                  }}
                >
                  <VibrationIcon
                    sx={{
                      fontSize: 14,
                      color: nearbyPromoActive || yaoyaoEnabled ? '#fbbf24' : 'rgba(196, 220, 233, 0.7)',
                      animation:
                        nearbyPromoActive || yaoyaoEnabled
                          ? 'mrPhoneVibrate 0.9s ease-in-out infinite'
                          : 'none',
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: '0.04em',
                      color: nearbyPromoActive || yaoyaoEnabled ? '#fbbf24' : 'rgba(222, 238, 246, 0.8)',
                    }}
                  >
                    {nearbyPromoLocating ? 'YaoYao…' : 'YaoYao'}
                  </Typography>
                </Box>
              </Tooltip>
              <Tooltip
                title={
                  yaoyaoEnabled
                    ? 'Shake is on — disable so shaking will not scan'
                    : 'Enable shake to activate YaoYao (iPhone will ask for motion access)'
                }
                placement="left"
              >
                <Switch
                  size="small"
                  checked={yaoyaoEnabled}
                  disabled={researchToolsDisabled || yaoyaoPermissionBusy}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(_, checked) => {
                    void handleYaoyaoToggle(checked);
                  }}
                  inputProps={{ 'aria-label': yaoyaoEnabled ? 'Disable YaoYao shake' : 'Enable YaoYao shake' }}
                  sx={{
                    flexShrink: 0,
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#fbbf24' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      bgcolor: '#fbbf24',
                    },
                  }}
                />
              </Tooltip>
            </Box>
          )}
          {canFilterThumbsup && (
            <Tooltip
              title={
                researchToolsDisabled
                  ? 'Turn off Super Ratio to use Thumbsup'
                  : 'Thumbsup'
              }
              placement="left"
            >
              <span>
                <Chip
                  clickable={!researchToolsDisabled}
                  disabled={researchToolsDisabled}
                  icon={<ThumbUpIcon sx={{ fontSize: 15 }} />}
                  label="Thumbsup"
                  onClick={() => {
                    if (researchToolsDisabled) return;
                    if (nearbyPromoActive) exitNearbyPromo();
                    setDealFilter((f) => (f === 'thumbsup' ? 'none' : 'thumbsup'));
                  }}
                  sx={{
                    ...dealChipSx(dealFilter === 'thumbsup', '#4ade80', researchToolsDisabled),
                    width: '100%',
                    justifyContent: 'flex-start',
                  }}
                />
              </span>
            </Tooltip>
          )}
        </Box>

        <Tooltip
          title={
            !canUseSuperRatio
              ? 'Super Ratio is loading'
              : researchToolsActive
                ? 'Clear Unit Criteria / deal filters to use Super Ratio'
                : superRatioActive
                  ? 'Hide Super Ratio'
                  : focus
                    ? 'Show Super Ratio (MOP / SSD pool)'
                    : 'Show Super Ratio for the whole of Singapore'
          }
          placement="left"
        >
          <Box sx={{ display: { xs: 'none', md: 'block' }, mt: 0.5, minWidth: 132 }}>
            <Chip
              clickable={!superRatioDisabled || superRatioActive}
              disabled={superRatioDisabled && !superRatioActive}
              icon={<BalanceIcon sx={{ fontSize: 15 }} />}
              label="Super Ratio"
              onClick={() => {
                if (superRatioActive) {
                  deactivateSuperRatio();
                  return;
                }
                if (superRatioDisabled) return;
                activateSuperRatio();
              }}
              sx={{
                ...dealChipSx(superRatioActive, '#ed8936', superRatioDisabled && !superRatioActive),
                width: '100%',
                justifyContent: 'flex-start',
              }}
            />
          </Box>
        </Tooltip>
      </Box>

      {/* ── Mobile YaoYao — scan action | shake preference, separate targets ─ */}
      {canFilterPricePromo && (
        <Paper
          sx={{
            ...glassPanelSx,
            display: { xs: 'flex', md: 'none' },
            position: 'absolute',
            left: 10,
            right: 10,
            bottom: 66,
            zIndex: 1000,
            p: 0.45,
            borderRadius: '16px',
            alignItems: 'stretch',
            gap: 0.45,
            opacity: researchToolsDisabled ? 0.45 : 1,
          }}
        >
          <Box
            component="button"
            type="button"
            disabled={researchToolsDisabled || nearbyPromoLocating}
            onClick={() => {
              if (researchToolsDisabled || nearbyPromoLocating) return;
              toggleNearbyPromo();
            }}
            sx={{
              appearance: 'none',
              border: '1px solid',
              borderColor: nearbyPromoActive ? 'rgba(251, 191, 36, 0.7)' : 'transparent',
              bgcolor: nearbyPromoActive ? 'rgba(251, 191, 36, 0.2)' : 'transparent',
              borderRadius: '12px',
              px: 1.1,
              py: 0.65,
              m: 0,
              minHeight: 48,
              cursor: researchToolsDisabled || nearbyPromoLocating ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.85,
              minWidth: 0,
              flex: 1,
              textAlign: 'left',
              color: 'inherit',
            }}
          >
            <NearMeIcon
              sx={{
                fontSize: 20,
                color: nearbyPromoActive ? '#fbbf24' : 'rgba(196, 220, 233, 0.75)',
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: nearbyPromoActive ? '#fbbf24' : '#eef6fa',
                  lineHeight: 1.15,
                }}
              >
                {nearbyPromoLocating ? 'YaoYao…' : nearbyPromoActive ? 'Nearby Price Promos' : 'YaoYao'}
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: nearbyPromoActive ? 'rgba(253, 230, 138, 0.85)' : 'rgba(196, 220, 233, 0.62)',
                  lineHeight: 1.2,
                }}
              >
                {nearbyPromoLocating
                  ? 'Locating you…'
                  : nearbyPromoActive
                    ? 'Tap to exit'
                    : 'Tap for promos within 5 km'}
              </Typography>
            </Box>
          </Box>
          <Box
            component="button"
            type="button"
            disabled={researchToolsDisabled || yaoyaoPermissionBusy}
            aria-pressed={yaoyaoEnabled}
            aria-label={yaoyaoEnabled ? 'Disable shake to scan' : 'Enable shake to scan'}
            onClick={() => {
              if (researchToolsDisabled || yaoyaoPermissionBusy) return;
              void handleYaoyaoToggle(!yaoyaoEnabled);
            }}
            sx={{
              appearance: 'none',
              border: '1px solid',
              borderColor: yaoyaoEnabled ? 'rgba(251, 191, 36, 0.65)' : 'rgba(34, 211, 238, 0.22)',
              bgcolor: yaoyaoEnabled ? 'rgba(251, 191, 36, 0.18)' : 'rgba(8, 22, 34, 0.35)',
              borderRadius: '12px',
              px: 1,
              py: 0.5,
              m: 0,
              width: 72,
              minHeight: 48,
              flexShrink: 0,
              cursor: researchToolsDisabled || yaoyaoPermissionBusy ? 'not-allowed' : 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.1,
              color: 'inherit',
            }}
          >
            <VibrationIcon
              sx={{
                fontSize: 18,
                color: yaoyaoEnabled ? '#fbbf24' : 'rgba(196, 220, 233, 0.55)',
                animation: yaoyaoEnabled ? 'mrPhoneVibrate 0.9s ease-in-out infinite' : 'none',
              }}
            />
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: yaoyaoEnabled ? '#fbbf24' : 'rgba(196, 220, 233, 0.55)',
                lineHeight: 1.1,
              }}
            >
              Shake
            </Typography>
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                color: yaoyaoEnabled ? 'rgba(253, 230, 138, 0.9)' : 'rgba(196, 220, 233, 0.45)',
                lineHeight: 1,
              }}
            >
              {yaoyaoEnabled ? 'On' : 'Off'}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* ── Mobile tool dock (bottom) — frees the map from the right chip stack ─ */}
      <Paper
        sx={{
          ...glassPanelSx,
          display: { xs: 'flex', md: 'none' },
          position: 'absolute',
          left: 10,
          right: 10,
          bottom: 10,
          zIndex: 1000,
          px: 0.75,
          py: 0.55,
          borderRadius: '14px',
          alignItems: 'center',
          justifyContent: 'space-around',
          gap: 0.25,
        }}
      >
        {(
          [
            {
              key: 'criteria',
              title: researchToolsDisabled ? 'Turn off Super Ratio to use Unit Criteria' : 'Unit Criteria',
              active: Boolean(criteriaAnchor) || criteriaActive,
              color: '#22d3ee',
              disabled: researchToolsDisabled,
              icon: <TuneIcon sx={{ fontSize: 18 }} />,
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                if (researchToolsDisabled) return;
                setCriteriaAnchor(e.currentTarget);
              },
            },
            ...(canFilterPricePromo
              ? [
                  {
                    key: 'promo',
                    title: researchToolsDisabled
                      ? 'Turn off Super Ratio to use Price Promo'
                      : nearbyPromoActive
                        ? 'Exit nearby Price Promo'
                        : 'Price Promo',
                    active: dealFilter === 'promo',
                    color: '#fbbf24',
                    disabled: researchToolsDisabled,
                    icon: <LocalOfferIcon sx={{ fontSize: 18 }} />,
                    onClick: () => {
                      if (researchToolsDisabled) return;
                      if (nearbyPromoActive) {
                        exitNearbyPromo();
                        return;
                      }
                      setDealFilter((f) => (f === 'promo' ? 'none' : 'promo'));
                    },
                  },
                ]
              : []),
            ...(canFilterThumbsup
              ? [
                  {
                    key: 'thumb',
                    title: researchToolsDisabled ? 'Turn off Super Ratio to use Thumbsup' : 'Thumbsup',
                    active: dealFilter === 'thumbsup',
                    color: '#4ade80',
                    disabled: researchToolsDisabled,
                    icon: <ThumbUpIcon sx={{ fontSize: 18 }} />,
                    onClick: () => {
                      if (researchToolsDisabled) return;
                      if (nearbyPromoActive) exitNearbyPromo();
                      setDealFilter((f) => (f === 'thumbsup' ? 'none' : 'thumbsup'));
                    },
                  },
                ]
              : []),
            {
              key: 'super-ratio',
              title: !canUseSuperRatio
                ? 'Super Ratio is loading'
                : researchToolsActive
                  ? 'Clear filters to use Super Ratio'
                  : superRatioActive
                    ? 'Hide Super Ratio'
                    : focus
                      ? 'Super Ratio'
                      : 'Super Ratio · whole of Singapore',
              active: superRatioActive,
              color: '#ed8936',
              disabled: superRatioDisabled && !superRatioActive,
              icon: <BalanceIcon sx={{ fontSize: 18 }} />,
              onClick: () => {
                if (superRatioActive) {
                  deactivateSuperRatio();
                  return;
                }
                if (superRatioDisabled) return;
                activateSuperRatio();
              },
            },
          ] as Array<{
            key: string;
            title: string;
            active: boolean;
            color: string;
            disabled: boolean;
            icon: React.ReactNode;
            onClick: (e: React.MouseEvent<HTMLElement>) => void;
          }>
        ).map((tool) => (
          <Tooltip key={tool.key} title={tool.title} placement="top">
            <span>
              <IconButton
                size="small"
                disabled={tool.disabled}
                onClick={tool.onClick}
                aria-label={tool.title}
                aria-pressed={tool.active}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '11px',
                  color: tool.active ? '#04121d' : tool.color,
                  bgcolor: tool.active ? tool.color : 'transparent',
                  border: '1px solid',
                  borderColor: tool.active ? tool.color : 'rgba(34, 211, 238, 0.22)',
                  opacity: tool.disabled ? 0.4 : 1,
                  '&:hover': {
                    bgcolor: tool.disabled
                      ? tool.active
                        ? tool.color
                        : 'transparent'
                      : tool.active
                        ? tool.color
                        : 'rgba(34, 211, 238, 0.1)',
                  },
                }}
              >
                {tool.icon}
              </IconButton>
            </span>
          </Tooltip>
        ))}
      </Paper>

      {/* ── Flash status bar with inline speed (top-center) ─────────── */}
      {flashRunning && flashPlans.length === 2 && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1001,
            display: { xs: 'none', md: 'flex' },
          }}
        >
          <Paper
            sx={{
              ...glassPanelSx,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.55,
              minWidth: 420,
            }}
          >
            <BoltIcon sx={{ fontSize: 16, color: '#fbbf24' }} />
            <Typography sx={{ ...microLabelSx, color: '#fbbf24' }}>Flash</Typography>
            {flashPlans.map((mp, idx) => (
              <Chip
                key={mp.id}
                size="small"
                label={mp.label}
                sx={{
                  height: 20,
                  fontSize: 10.5,
                  fontWeight: 800,
                  color: idx === flashIndex ? '#04121d' : 'rgba(238,246,250,0.75)',
                  bgcolor: idx === flashIndex ? '#22d3ee' : 'rgba(34, 211, 238, 0.12)',
                  transition: 'all 0.2s',
                }}
              />
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 140, mx: 0.5 }}>
              <Typography sx={{ fontSize: 9.5, color: 'rgba(196, 220, 233, 0.55)', flexShrink: 0 }}>Slow</Typography>
              <Slider
                size="small"
                value={FLASH_INTERVAL_MAX_MS + FLASH_INTERVAL_MIN_MS - flashIntervalMs}
                min={FLASH_INTERVAL_MIN_MS}
                max={FLASH_INTERVAL_MAX_MS}
                step={50}
                onChange={(_e, value) =>
                  setFlashIntervalMs(FLASH_INTERVAL_MAX_MS + FLASH_INTERVAL_MIN_MS - (value as number))
                }
                sx={{
                  color: '#fbbf24',
                  py: 0.5,
                  '& .MuiSlider-thumb': { width: 12, height: 12, boxShadow: '0 0 8px rgba(251, 191, 36, 0.8)' },
                  '& .MuiSlider-rail': { opacity: 0.35 },
                }}
              />
              <Typography sx={{ fontSize: 9.5, color: 'rgba(196, 220, 233, 0.55)', flexShrink: 0 }}>Fast</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#fbbf24', minWidth: 32, textAlign: 'right' }}>
                {(flashIntervalMs / 1000).toFixed(1)}s
              </Typography>
            </Box>
            <IconButton size="small" onClick={stopFlash} sx={{ color: '#f87171' }}>
              <StopIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Paper>
        </Box>
      )}

      {/* ── Compare cart launcher (always available) ──────────────── */}
      <Tooltip title="Open compare cart" placement="right">
        <IconButton
          ref={compareFabRef}
          onClick={() => setCompareCartOpen(true)}
          sx={{
            ...glassPanelSx,
            position: 'absolute',
            // Sit above the mobile tool dock; desktop keeps the old bottom-left slot.
            bottom: {
              xs: mobileBottomChrome,
              md: superRatioActive ? 22 : 118,
            },
            left: { xs: 'auto', md: 12 },
            right: { xs: 12, md: 'auto' },
            zIndex: 1000,
            width: { xs: 40, md: 44 },
            height: { xs: 40, md: 44 },
            borderRadius: '12px',
            color: '#c4b5fd',
            borderColor: 'rgba(167, 139, 250, 0.45)',
            animation: compareFabPulse ? 'mrCompareFabPulse 0.9s cubic-bezier(0.3, 1.3, 0.5, 1)' : 'none',
            '&:hover': { bgcolor: 'rgba(20, 36, 56, 0.92)' },
          }}
          aria-label="Open compare cart"
        >
          <Badge
            badgeContent={compareCart.length}
            color="secondary"
            sx={{
              '& .MuiBadge-badge': {
                bgcolor: '#a78bfa',
                color: '#04121d',
                fontWeight: 800,
                fontSize: 10,
              },
            }}
          >
            <ShoppingCartOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      {compareThrow && (
        <Box
          key={compareThrow.key}
          sx={{
            position: 'fixed',
            left: compareThrow.startX,
            top: compareThrow.startY,
            zIndex: 2000,
            pointerEvents: 'none',
            // Centering lives here: the inner layers own transform for the toss.
            transform: 'translate(-50%, -50%)',
            ['--mr-throw-dx' as string]: `${compareThrow.endX - compareThrow.startX}px`,
            ['--mr-throw-dy' as string]: `${compareThrow.endY - compareThrow.startY}px`,
            ['--mr-throw-lift' as string]: `${compareThrow.lift}px`,
          }}
        >
          <Box sx={{ animation: 'mrThrowX 0.95s cubic-bezier(0.24, 0.62, 0.36, 1) forwards' }}>
            <Box sx={{ animation: 'mrThrowY 0.95s linear forwards' }}>
              <Box
                sx={{
                  position: 'relative',
                  animation: 'mrThrowChip 0.95s cubic-bezier(0.4, 0, 0.6, 1) forwards',
                }}
              >
                {/* Soft trail so the eye can follow the toss */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: -10,
                    borderRadius: '999px',
                    background: 'radial-gradient(circle, rgba(167, 139, 250, 0.55) 0%, transparent 70%)',
                    animation: 'mrThrowTrail 0.95s ease-out forwards',
                  }}
                />
                <Box
                  sx={{
                    position: 'relative',
                    px: 1.1,
                    py: 0.55,
                    borderRadius: '999px',
                    background: 'linear-gradient(135deg, #ddd6fe 0%, #a78bfa 100%)',
                    color: '#1b1035',
                    fontSize: 11,
                    fontWeight: 800,
                    maxWidth: 170,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    border: '1px solid rgba(255, 255, 255, 0.75)',
                    boxShadow: '0 10px 28px rgba(76, 29, 149, 0.5), 0 0 16px rgba(167, 139, 250, 0.6)',
                  }}
                >
                  {compareThrow.label}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Legend + count (bottom-left) ────────────────────────────── */}
      {!superRatioActive && (
      <Paper
        sx={{
          ...glassPanelSx,
          position: 'absolute',
          bottom: { xs: mobileBottomChrome, md: 22 },
          left: 12,
          right: { xs: 60, md: 'auto' },
          zIndex: 1000,
          px: { xs: 1.1, md: 1.5 },
          py: { xs: 0.65, md: 0.9 },
          display: 'flex',
          flexDirection: { xs: 'row', md: 'column' },
          alignItems: { xs: 'center', md: 'stretch' },
          gap: { xs: 0.75, md: 0.4 },
          maxWidth: { xs: 'none', md: 280 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: { xs: 1, md: 'none' } }}>
          <Box className="mr-pulse-dot" sx={{ flexShrink: 0 }} />
          <Typography
            sx={{
              ...microLabelSx,
              color: '#22d3ee',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {projectsLoading || criteriaLoading
              ? 'Scanning…'
              : dealMetaLoading
                ? `${visibleProjects.length} projects · refining…`
                : criteriaActive
                ? `${visibleProjects.length} · ${
                    dealFilter === 'promo'
                      ? visibleProjects.reduce(
                          (sum, p) => sum + (criteriaDealProjectMeta.promoCounts.get(p.id) ?? 0),
                          0,
                        )
                      : dealFilter === 'thumbsup'
                        ? visibleProjects.reduce(
                            (sum, p) => sum + (criteriaDealProjectMeta.thumbCounts.get(p.id) ?? 0),
                            0,
                          )
                        : matchedUnitTotal
                  } units`
                : `${visibleProjects.length} plotted`}
          </Typography>
        </Box>
        {(
          markerKind === 'promo'
            ? [{
                color: '#f59e0b',
                label: nearbyPromoActive ? `Nearby · ${radiusKm} km` : 'Price Promo',
                radius: '4px',
              }]
            : markerKind === 'thumb'
              ? [{ color: '#22c55e', label: 'Thumbsup', radius: '4px' }]
              : [{ color: '#3b82f6', label: 'Project', radius: '4px' }]
        ).map((row) => (
          <Box
            key={row.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: row.radius,
                bgcolor: row.color,
                boxShadow: `0 0 6px ${row.color}`,
                border: '1px solid rgba(255,255,255,0.7)',
              }}
            />
            <Typography
              sx={{
                fontSize: 10.5,
                color: 'rgba(222, 238, 246, 0.8)',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {row.label}
            </Typography>
          </Box>
        ))}
      </Paper>
      )}

      {/* ── Loading / error overlays ────────────────────────────────── */}
      {(projectsLoading || criteriaLoading) && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1100,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            background:
              'radial-gradient(ellipse at center, rgba(8, 28, 48, 0.72) 0%, rgba(4, 10, 20, 0.85) 70%)',
            backdropFilter: 'blur(4px)',
            pointerEvents: 'none',
            overflow: 'hidden',
            '@keyframes mrOverlaySweep': {
              '0%': { transform: 'translateY(-30%)' },
              '100%': { transform: 'translateY(130%)' },
            },
            '@keyframes mrOverlayBar': {
              '0%': { width: '22%' },
              '50%': { width: '86%' },
              '100%': { width: '38%' },
            },
            '@keyframes mrOverlayPulse': {
              '0%, 100%': { opacity: 0.35, transform: 'scale(0.92)' },
              '50%': { opacity: 1, transform: 'scale(1)' },
            },
            '@keyframes mrOverlayBlink': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.25 },
            },
            '@keyframes mrOverlayGlow': {
              '0%, 100%': { opacity: 0.55, letterSpacing: '0.14em' },
              '50%': { opacity: 1, letterSpacing: '0.2em' },
            },
          }}
        >
          {/* Horizontal scan beam */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              height: '28%',
              background:
                'linear-gradient(180deg, transparent 0%, rgba(34, 211, 238, 0.06) 40%, rgba(103, 232, 249, 0.16) 50%, rgba(34, 211, 238, 0.06) 60%, transparent 100%)',
              animation: 'mrOverlaySweep 2.8s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />

          <Box
            sx={{
              position: 'relative',
              width: { xs: 'min(88vw, 360px)', sm: 380 },
              px: 2.25,
              py: 2,
              borderRadius: '14px',
              border: '1px solid rgba(34, 211, 238, 0.3)',
              background:
                'linear-gradient(160deg, rgba(10, 22, 36, 0.92) 0%, rgba(8, 18, 32, 0.88) 100%)',
              boxShadow: '0 0 40px rgba(34, 211, 238, 0.12), inset 0 1px 0 rgba(255,255,255,0.05)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, mb: 1.25 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#22d3ee',
                  boxShadow: '0 0 10px 2px rgba(34, 211, 238, 0.75)',
                  animation: 'mrOverlayBlink 1.1s ease-in-out infinite',
                }}
              />
              <Typography
                sx={{
                  ...microLabelSx,
                  fontSize: 11,
                  color: '#22d3ee',
                  animation: 'mrOverlayGlow 2s ease-in-out infinite',
                }}
              >
                {criteriaLoading ? 'Running unit criteria scan…' : 'Scanning project database…'}
              </Typography>
            </Box>

            <Typography sx={{ fontSize: 12, color: 'rgba(165, 243, 252, 0.72)', fontWeight: 600, mb: 1.5 }}>
              {criteriaLoading
                ? 'Matching available inventory across Singapore'
                : 'Plotting active projects on the grid'}
            </Typography>

            {/* Progress rail */}
            <Box
              sx={{
                height: 5,
                borderRadius: 3,
                bgcolor: 'rgba(34, 211, 238, 0.12)',
                overflow: 'hidden',
                mb: 1.75,
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  background: 'linear-gradient(90deg, #0891b2 0%, #22d3ee 45%, #a5f3fc 100%)',
                  boxShadow: '0 0 14px rgba(34, 211, 238, 0.65)',
                  animation: 'mrOverlayBar 2.4s ease-in-out infinite',
                }}
              />
            </Box>

            {/* Signal grid — futuristic, not a spinner */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(8, 1fr)',
                gap: 0.6,
              }}
            >
              {Array.from({ length: 24 }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    height: 10,
                    borderRadius: '3px',
                    bgcolor: 'rgba(34, 211, 238, 0.18)',
                    boxShadow: '0 0 6px rgba(34, 211, 238, 0.15)',
                    animation: 'mrOverlayPulse 1.5s ease-in-out infinite',
                    animationDelay: `${(i % 8) * 0.08 + Math.floor(i / 8) * 0.12}s`,
                  }}
                />
              ))}
            </Box>

            <Typography
              sx={{
                mt: 1.5,
                fontFamily: '"SF Mono", "Roboto Mono", Menlo, monospace',
                fontSize: 9.5,
                letterSpacing: '0.1em',
                color: 'rgba(148, 197, 222, 0.45)',
              }}
            >
              MAP RESEARCH · LIVE GRID · {criteriaLoading ? 'CRITERIA MODE' : 'BASELINE PLOT'}
            </Typography>
          </Box>
        </Box>
      )}

      {nearbyPromoLocating && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1100,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            background:
              'radial-gradient(ellipse at center, rgba(8, 28, 48, 0.72) 0%, rgba(4, 10, 20, 0.85) 70%)',
            backdropFilter: 'blur(4px)',
            pointerEvents: 'none',
            overflow: 'hidden',
            '@keyframes mrOverlaySweep': {
              '0%': { transform: 'translateY(-30%)' },
              '100%': { transform: 'translateY(130%)' },
            },
            '@keyframes mrOverlayBlink': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.25 },
            },
            '@keyframes mrOverlayGlow': {
              '0%, 100%': { opacity: 0.55, letterSpacing: '0.14em' },
              '50%': { opacity: 1, letterSpacing: '0.2em' },
            },
            '@keyframes mrRadarSweep': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
            '@keyframes mrRadarPing': {
              '0%': { transform: 'scale(0.28)', opacity: 0.65 },
              '100%': { transform: 'scale(1)', opacity: 0 },
            },
            '@keyframes mrRadarDot': {
              '0%, 100%': { opacity: 0.3 },
              '45%': { opacity: 1 },
            },
          }}
        >
          {/* Horizontal scan beam — same as project-database overlay */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              height: '28%',
              background:
                'linear-gradient(180deg, transparent 0%, rgba(34, 211, 238, 0.06) 40%, rgba(103, 232, 249, 0.16) 50%, rgba(34, 211, 238, 0.06) 60%, transparent 100%)',
              animation: 'mrOverlaySweep 2.8s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />

          <Box
            sx={{
              position: 'relative',
              width: { xs: 'min(88vw, 360px)', sm: 380 },
              px: 2.25,
              py: 2,
              borderRadius: '14px',
              border: '1px solid rgba(34, 211, 238, 0.3)',
              background:
                'linear-gradient(160deg, rgba(10, 22, 36, 0.92) 0%, rgba(8, 18, 32, 0.88) 100%)',
              boxShadow: '0 0 40px rgba(34, 211, 238, 0.12), inset 0 1px 0 rgba(255,255,255,0.05)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, mb: 1.25 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#22d3ee',
                  boxShadow: '0 0 10px 2px rgba(34, 211, 238, 0.75)',
                  animation: 'mrOverlayBlink 1.1s ease-in-out infinite',
                }}
              />
              <Typography
                sx={{
                  ...microLabelSx,
                  fontSize: 11,
                  color: '#22d3ee',
                  animation: 'mrOverlayGlow 2s ease-in-out infinite',
                }}
              >
                YaoYao
              </Typography>
            </Box>

            <Typography sx={{ fontSize: 12, color: 'rgba(165, 243, 252, 0.72)', fontWeight: 600, mb: 1.5 }}>
              Scanning nearby Price Promos…
            </Typography>

            {/* Radar scan — replaces the signal grid */}
            <Box
              sx={{
                position: 'relative',
                width: 132,
                height: 132,
                mx: 'auto',
                mb: 1.5,
                borderRadius: '50%',
                border: '1px solid rgba(34, 211, 238, 0.35)',
                background:
                  'radial-gradient(circle at center, rgba(34, 211, 238, 0.14) 0%, rgba(8, 18, 32, 0.65) 58%, rgba(4, 10, 20, 0.9) 100%)',
                boxShadow: '0 0 24px rgba(34, 211, 238, 0.18), inset 0 0 20px rgba(34, 211, 238, 0.08)',
                overflow: 'hidden',
              }}
            >
              {[24, 44, 62].map((size) => (
                <Box
                  key={size}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: size * 2,
                    height: size * 2,
                    ml: `-${size}px`,
                    mt: `-${size}px`,
                    borderRadius: '50%',
                    border: '1px solid rgba(34, 211, 238, 0.2)',
                  }}
                />
              ))}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(90deg, transparent 49%, rgba(34, 211, 238, 0.22) 50%, transparent 51%), linear-gradient(180deg, transparent 49%, rgba(34, 211, 238, 0.22) 50%, transparent 51%)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: 132,
                  height: 132,
                  ml: '-66px',
                  mt: '-66px',
                  borderRadius: '50%',
                  border: '1px solid rgba(103, 232, 249, 0.55)',
                  animation: 'mrRadarPing 2.1s ease-out infinite',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  background:
                    'conic-gradient(from 0deg, transparent 0deg, transparent 295deg, rgba(34, 211, 238, 0.06) 325deg, rgba(103, 232, 249, 0.55) 360deg)',
                  animation: 'mrRadarSweep 2.2s linear infinite',
                }}
              />
              {[
                { top: '27%', left: '63%' },
                { top: '57%', left: '29%' },
                { top: '69%', left: '68%' },
              ].map((pos, i) => (
                <Box
                  key={i}
                  sx={{
                    position: 'absolute',
                    top: pos.top,
                    left: pos.left,
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    bgcolor: '#67e8f9',
                    boxShadow: '0 0 8px rgba(103, 232, 249, 0.95)',
                    animation: 'mrRadarDot 1.7s ease-in-out infinite',
                    animationDelay: `${i * 0.3}s`,
                  }}
                />
              ))}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: 9,
                  height: 9,
                  ml: '-4.5px',
                  mt: '-4.5px',
                  borderRadius: '50%',
                  bgcolor: '#22d3ee',
                  border: '1.5px solid rgba(255,255,255,0.85)',
                  boxShadow: '0 0 12px rgba(34, 211, 238, 0.95)',
                }}
              />
            </Box>

            <Typography
              sx={{
                mt: 0.25,
                fontFamily: '"SF Mono", "Roboto Mono", Menlo, monospace',
                fontSize: 9.5,
                letterSpacing: '0.1em',
                color: 'rgba(148, 197, 222, 0.45)',
                textAlign: 'center',
              }}
            >
              SUPER MAP · NEARBY PROMO · {NEARBY_PROMO_RADIUS_KM} KM RADIUS
            </Typography>
          </Box>
        </Box>
      )}

      {projectsError && !projectsLoading && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void fetchProjects()}>
              Retry
            </Button>
          }
          sx={{ position: 'absolute', top: 70, left: '50%', transform: 'translateX(-50%)', zIndex: 1100, borderRadius: '10px' }}
        >
          {projectsError}
        </Alert>
      )}

      {/* ── Bottom-right: project console + separate Super Ratio detail card ─ */}
      {(selected || (superRatioSelection && superRatioData)) && (
        <Box
          sx={{
            position: 'absolute',
            // Clear the mobile tool dock; desktop keeps the bottom-right console.
            bottom: { xs: mobileBottomChrome, md: 22 },
            right: { xs: 8, md: 12 },
            left: { xs: 8, md: 'auto' },
            zIndex: 1200,
            maxHeight: { xs: '58%', md: 'calc(100% - 110px)' },
            width: { xs: 'auto', md: 396 },
            maxWidth: 396,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minHeight: 0,
          }}
        >
          {selected && (
            <Box
              sx={{
                flex: projectBodyCollapsed && superRatioSelection ? '0 0 auto' : '0 1 auto',
                minHeight: 0,
                maxHeight: projectBodyCollapsed && superRatioSelection ? 'none' : '100%',
                display: 'flex',
                overflow: 'hidden',
              }}
            >
              <ProjectPopupCard
                project={selected.project}
                pricePromoUnits={promoUnitsByProject.get(selected.id) ?? []}
                thumbsupUnits={thumbUnitsByProject.get(selected.id) ?? []}
                onClose={() => {
                  setSelectedId(null);
                  setMatchedUnits([]);
                  setMatchedUnitsError(null);
                  turnOffSuperRatio();
                }}
                onViewDetails={onViewProject}
                onPricePromoUnitClick={onPricePromoUnitClick}
                onThumbsupUnitClick={onThumbsupUnitClick}
                onSuperRatio={
                  canUseSuperRatio
                    ? () => {
                        if (superRatioActive) turnOffSuperRatio();
                        else launchSuperRatioAtProject(selected.project);
                      }
                    : undefined
                }
                superRatioActive={superRatioActive}
                onToggleCompare={(event) => toggleCompareProject(selected.project, event)}
                compareInCart={compareCart.some((p) => p.id === selected.id)}
                compareCartFull={compareCart.length >= COMPARE_CART_MAX}
                searchResultUnits={
                  criteriaActive
                    ? dealFilter === 'promo'
                      ? matchedUnits.filter((unit) => promoUnitIds.has(unit.id))
                      : dealFilter === 'thumbsup'
                        ? matchedUnits.filter((unit) => thumbsupUnitIds.has(unit.id))
                        : matchedUnits
                    : undefined
                }
                searchResultsLoading={criteriaActive ? matchedUnitsLoading : false}
                searchResultsError={criteriaActive ? matchedUnitsError : null}
                promoUnitIds={promoUnitIds}
                thumbsupUnitIds={thumbsupUnitIds}
                initialTab={criteriaActive ? 'search' : 'details'}
                bodyCollapsed={projectBodyCollapsed}
                onToggleBodyCollapsed={
                  superRatioSelection && superRatioData
                    ? () => setProjectBodyCollapsed((v) => !v)
                    : undefined
                }
              />
            </Box>
          )}

          {superRatioSelection && superRatioData && (
            <Box
              sx={{
                flex: '1 1 auto',
                minHeight: 0,
                display: 'flex',
                overflow: 'hidden',
              }}
            >
              <DemandSupplyDetailCard
                variant="mapResearch"
                selection={superRatioSelection}
                data={superRatioData}
                onClose={() => setSuperRatioSelection(null)}
              />
            </Box>
          )}
        </Box>
      )}

      {/* ── Unit criteria picker ────────────────────────────────────── */}
      <Popover
        open={Boolean(criteriaAnchor)}
        anchorEl={criteriaAnchor}
        onClose={() => setCriteriaAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            ...glassPanelSx,
            mt: 1,
            p: 1.5,
            width: { xs: 'min(92vw, 360px)', sm: 360 },
            maxHeight: 'min(78vh, 640px)',
            overflow: 'auto',
          },
        }}
      >
        <MapResearchCriteriaPanel
          filters={criteriaFilters}
          onFiltersChange={setCriteriaFilters}
          loading={criteriaLoading}
          criteriaActive={criteriaActive}
          matchedProjectCount={projects.length}
          matchedUnitCount={matchedUnitTotal}
          onSearch={() => {
            setCriteriaAnchor(null);
            void handleCriteriaSearch();
          }}
          onClear={() => {
            handleCriteriaClear();
          }}
        />
      </Popover>

      {/* ── Basemap picker ──────────────────────────────────────────── */}
      <Popover
        open={Boolean(basemapAnchor)}
        anchorEl={basemapAnchor}
        onClose={() => setBasemapAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { ...glassPanelSx, mt: 1, p: 1.5, width: 236 } }}
      >
        <Typography sx={{ ...microLabelSx, color: '#22d3ee', mb: 1 }}>Basemap</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
          {BASEMAPS.map((option) => (
            <Box
              key={option.id}
              onClick={() => {
                setBasemapId(option.id);
                setBasemapAnchor(null);
              }}
              sx={{
                cursor: 'pointer',
                borderRadius: '10px',
                p: 0.75,
                border: `1px solid ${option.id === basemapId ? '#22d3ee' : 'rgba(34, 211, 238, 0.18)'}`,
                bgcolor: option.id === basemapId ? 'rgba(34, 211, 238, 0.12)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(34, 211, 238, 0.08)' },
              }}
            >
              <Box
                sx={{
                  height: 26,
                  borderRadius: '6px',
                  background: `linear-gradient(120deg, ${option.preview[0]} 0%, ${option.preview[1]} 100%)`,
                  border: '1px solid rgba(255,255,255,0.12)',
                }}
              />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#eef6fa', mt: 0.5, textAlign: 'center' }}>
                {option.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>

      {/* ── Master plan panel ───────────────────────────────────────── */}
      <Popover
        open={Boolean(masterPlanAnchor)}
        anchorEl={masterPlanAnchor}
        onClose={() => setMasterPlanAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { ...glassPanelSx, mt: 1, p: 1.75, width: 288 } }}
      >
        <Typography sx={{ ...microLabelSx, color: '#22d3ee' }}>URA Master Plan Overlay</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 1 }}>
          <Chip
            size="small"
            label="Off"
            clickable
            onClick={() => {
              setMasterPlanId(null);
              setFlashRunning(false);
            }}
            sx={{
              fontWeight: 700,
              fontSize: 11,
              color: !masterPlanId && !flashRunning ? '#04121d' : 'rgba(238,246,250,0.8)',
              bgcolor: !masterPlanId && !flashRunning ? '#22d3ee' : 'rgba(34, 211, 238, 0.1)',
              border: '1px solid rgba(34, 211, 238, 0.3)',
            }}
          />
          {MASTER_PLANS.map((mp) => {
            const active = !flashRunning && masterPlanId === mp.id;
            return (
              <Tooltip key={mp.id} title={mp.description}>
                <Chip
                  size="small"
                  label={mp.label}
                  clickable
                  onClick={() => {
                    setMasterPlanId(mp.id);
                    setFlashRunning(false);
                  }}
                  sx={{
                    fontWeight: 700,
                    fontSize: 11,
                    color: active ? '#04121d' : 'rgba(238,246,250,0.8)',
                    bgcolor: active ? '#22d3ee' : 'rgba(34, 211, 238, 0.1)',
                    border: '1px solid rgba(34, 211, 238, 0.3)',
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
        <Typography sx={{ fontSize: 10, color: 'rgba(196, 220, 233, 0.55)', mt: 0.75 }}>
          * Master Plan 2025 with Approved Amendments Incorporated
        </Typography>

        {(masterPlanId || flashRunning) && (
          <Box sx={{ mt: 1.25 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={microLabelSx}>Overlay Opacity</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#22d3ee' }}>
                {Math.round(masterPlanOpacity * 100)}%
              </Typography>
            </Box>
            <Slider
              size="small"
              value={masterPlanOpacity}
              min={0.2}
              max={1}
              step={0.05}
              onChange={(_e, value) => setMasterPlanOpacity(value as number)}
              sx={{ color: '#22d3ee' }}
            />
          </Box>
        )}

        <Divider sx={{ my: 1.25, borderColor: 'rgba(34, 211, 238, 0.15)' }} />

        {/* Flash comparison — pick two plans, alternate at adjustable speed */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <BoltIcon sx={{ fontSize: 15, color: '#fbbf24' }} />
          <Typography sx={{ ...microLabelSx, color: '#fbbf24' }}>Flash Compare</Typography>
        </Box>
        <Typography sx={{ fontSize: 10.5, color: 'rgba(196, 220, 233, 0.6)', mt: 0.4 }}>
          Pick 2 master plans to flash between — spot land-use changes instantly.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 0.9 }}>
          {MASTER_PLANS.map((mp) => {
            const picked = flashPicks.includes(mp.id);
            return (
              <Chip
                key={`flash-pick-${mp.id}`}
                size="small"
                label={mp.label}
                clickable
                onClick={() => toggleFlashPick(mp.id)}
                sx={{
                  fontWeight: 700,
                  fontSize: 11,
                  color: picked ? '#04121d' : 'rgba(238,246,250,0.8)',
                  bgcolor: picked ? '#fbbf24' : 'rgba(251, 191, 36, 0.1)',
                  border: '1px solid rgba(251, 191, 36, 0.35)',
                }}
              />
            );
          })}
        </Box>
        <Box sx={{ mt: 1.25 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography sx={{ ...microLabelSx, color: '#fbbf24' }}>Flash Speed</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#fbbf24' }}>
              {(flashIntervalMs / 1000).toFixed(1)}s
            </Typography>
          </Box>
          <Slider
            size="small"
            // Invert so left = slower (higher ms), right = faster (lower ms)
            value={FLASH_INTERVAL_MAX_MS + FLASH_INTERVAL_MIN_MS - flashIntervalMs}
            min={FLASH_INTERVAL_MIN_MS}
            max={FLASH_INTERVAL_MAX_MS}
            step={50}
            onChange={(_e, value) =>
              setFlashIntervalMs(FLASH_INTERVAL_MAX_MS + FLASH_INTERVAL_MIN_MS - (value as number))
            }
            sx={{
              color: '#fbbf24',
              '& .MuiSlider-thumb': { boxShadow: '0 0 8px rgba(251, 191, 36, 0.8)' },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -0.5 }}>
            <Typography sx={{ fontSize: 9.5, color: 'rgba(196, 220, 233, 0.55)' }}>Slower</Typography>
            <Typography sx={{ fontSize: 9.5, color: 'rgba(196, 220, 233, 0.55)' }}>Faster</Typography>
          </Box>
        </Box>
        {flashRunning ? (
          <Button
            fullWidth
            size="small"
            startIcon={<StopIcon />}
            onClick={stopFlash}
            sx={{
              mt: 1.25,
              fontWeight: 800,
              color: '#f87171',
              border: '1px solid rgba(248, 113, 113, 0.5)',
              borderRadius: '9px',
            }}
          >
            Stop Flashing
          </Button>
        ) : (
          <Button
            fullWidth
            size="small"
            startIcon={<BoltIcon />}
            disabled={flashPicks.length !== 2}
            onClick={startFlash}
            sx={{
              mt: 1.25,
              fontWeight: 800,
              color: flashPicks.length === 2 ? '#04121d' : 'rgba(238,246,250,0.4)',
              background: flashPicks.length === 2 ? 'linear-gradient(90deg, #fbbf24, #f59e0b)' : 'rgba(251, 191, 36, 0.08)',
              borderRadius: '9px',
              '&:hover': { background: 'linear-gradient(90deg, #fcd34d, #fbbf24)' },
            }}
          >
            {flashPicks.length === 2 ? 'Start Flash' : `Pick ${2 - flashPicks.length} more plan${flashPicks.length === 1 ? '' : 's'}`}
          </Button>
        )}
      </Popover>

      <Dialog
        open={yaoyaoMotionHelpOpen}
        onClose={() => setYaoyaoMotionHelpOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            border: '1px solid rgba(251, 191, 36, 0.35)',
            bgcolor: 'rgba(8, 18, 32, 0.96)',
            color: '#eef6fa',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.75, fontWeight: 800, fontSize: 17, color: '#fbbf24' }}>
          Turn on Motion Access
        </DialogTitle>
        <DialogContent sx={{ pt: 1.25 }}>
          <Typography sx={{ fontSize: 13, color: 'rgba(222, 238, 246, 0.85)', mb: 1.5, lineHeight: 1.45 }}>
            Shake needs motion access on iPhone. If you denied it earlier, Safari will not ask again — enable it in Settings:
          </Typography>
          <Box
            component="ol"
            sx={{
              m: 0,
              pl: 2.25,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.85,
              '& li': {
                fontSize: 13,
                fontWeight: 600,
                color: '#eef6fa',
                lineHeight: 1.4,
                pl: 0.25,
              },
            }}
          >
            <li>Open the Settings app</li>
            <li>Tap Safari (or Chrome / your browser)</li>
            <li>Turn on Motion &amp; Orientation Access</li>
            <li>Return here and turn Shake on again</li>
          </Box>
          <Typography sx={{ fontSize: 12, color: 'rgba(196, 220, 233, 0.65)', mt: 1.5, lineHeight: 1.4 }}>
            Still blocked? In Safari Settings → Advanced → Website Data, remove this site, reload, then try Shake again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button
            onClick={() => setYaoyaoMotionHelpOpen(false)}
            variant="contained"
            sx={{
              fontWeight: 800,
              color: '#04121d',
              bgcolor: '#fbbf24',
              borderRadius: '10px',
              px: 2,
              '&:hover': { bgcolor: '#fcd34d' },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={yaoyaoShakeUndoHelpOpen}
        onClose={cancelYaoyaoShakeUndoHelp}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            border: '1px solid rgba(251, 191, 36, 0.35)',
            bgcolor: 'rgba(8, 18, 32, 0.96)',
            color: '#eef6fa',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.75, fontWeight: 800, fontSize: 17, color: '#fbbf24' }}>
          Turn off Shake to Undo
        </DialogTitle>
        <DialogContent sx={{ pt: 1.25 }}>
          <Typography sx={{ fontSize: 13, color: 'rgba(222, 238, 246, 0.85)', mb: 1.5, lineHeight: 1.45 }}>
            On iPhone, Shake to Undo is on by default and shows an “Undo Typing” popup when you shake.
            Turn it off before using YaoYao Shake — this app cannot disable it for you.
          </Typography>
          <Box
            component="ol"
            sx={{
              m: 0,
              pl: 2.25,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.85,
              '& li': {
                fontSize: 13,
                fontWeight: 600,
                color: '#eef6fa',
                lineHeight: 1.4,
                pl: 0.25,
              },
            }}
          >
            <li>Open the Settings app</li>
            <li>Tap Accessibility → Touch</li>
            <li>Turn off Shake to Undo</li>
            <li>Return here and confirm below</li>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button
            onClick={cancelYaoyaoShakeUndoHelp}
            sx={{ fontWeight: 700, color: 'rgba(222, 238, 246, 0.75)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmYaoyaoShakeUndoOff}
            variant="contained"
            sx={{
              fontWeight: 800,
              color: '#04121d',
              bgcolor: '#fbbf24',
              borderRadius: '10px',
              px: 2,
              '&:hover': { bgcolor: '#fcd34d' },
            }}
          >
            I&apos;ve turned it off
          </Button>
        </DialogActions>
      </Dialog>

      <CompareCartDialog
        open={compareCartOpen}
        projects={compareCart}
        onClose={() => setCompareCartOpen(false)}
        onRemove={removeCompareProject}
        onClear={clearCompareCart}
        radiusMeters={Math.round(radiusKm * 1000)}
        minRadiusMeters={superRatioMinRadiusMeters}
        canUseSuperRatio={canUseSuperRatio}
      />
    </Box>
  );
};

export default MapResearchTab;
